import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { announcerToneProfile, deriveAnnouncerCue, type AnnouncerTone } from '../shared/announcer';
import { readCameraSettings, subscribeCameraSettings, writeCameraSettings, type CameraSettings } from '../game/cameraSettings';
import { WEAPONS } from '../shared/constants';
import { getWeaponIdentity } from '../shared/game';
import { prioritizeHudEvents } from '../shared/hudPriority';
import { useGameStore, type CtfMacroSummary } from '../state/useGameStore';
import { routeZoneLabel } from '../shared/teamplay';
import { BaseSystemsPanel } from './BaseSystemsPanel';
import { HelpOverlay } from './HelpOverlay';
import { Radar } from './Radar';
import { Scoreboard } from './Scoreboard';
import { TutorialOverlay } from './TutorialOverlay';
import { WeaponPanel } from './WeaponPanel';
import { SettingsPanel } from './SettingsPanel';
import { TouchControlsOverlay } from './TouchControlsOverlay';
import { debugTimeline, type DebugTimelineFrame, type DebugTimelineSummary } from '../game/debugTimeline';
import { completeTutorialStep, dismissTutorial, getCurrentTutorialStep, readTutorialProgress, type TutorialStepId } from './tutorialProgress';
import type { BotIntentSnapshot, CombatEvent, WeaponDebugTelemetrySnapshot } from '../shared/protocol';
import type { MovementCue } from '../shared/movementTelemetry';
import { FloatingDamages } from './FloatingDamages';
import { KillStreakBanner } from './KillStreakBanner';
import { HitIndicator } from './HitIndicator';
import { SniperScope } from './SniperScope';
import { ChatOverlay } from './ChatOverlay';
import { CommandMap } from './CommandMap';
import { EnemyIFFBracket } from './EnemyIFFBracket';
import { LeadReticle } from './LeadReticle';
import { VoiceCommandMenu } from './VoiceCommandMenu';
import { ObjectiveHintBanner } from './ObjectiveHintBanner';
import { Nameplates } from './Nameplates';
import { sendMessage as networkSendMessage } from '../game/network';
import { resolveHealthBarColor, resolveHudColorModeTokens, resolveTeamAccentColor } from './hudDesign';

type Vec3Like = {
  x?: number;
  y?: number;
  z?: number;
};

type HudPlayerSnapshot = {
  id?: string;
  displayName?: string;
  isBot?: boolean;
  botRole?: string;
  botIntent?: BotIntentSnapshot;
  team?: string | null;
  armorClass?: string | null;
  selectedWeapon?: string | null;
  ads?: boolean;
  score?: number;
  health?: number;
  energy?: number;
  speed?: number;
  skiing?: boolean;
  isGrounded?: boolean;
  weaponHeat?: number;
  weaponCooldown?: number;
  weaponDebug?: WeaponDebugTelemetrySnapshot;
  overheated?: boolean;
  spawnProtected?: boolean;
  carryingFlagTeam?: string | null;
  position?: Vec3Like;
  yaw?: number;
  packSlot?: number | null;
  packId?: string | null;
  packLabel?: string | null;
  packType?: string | null;
  packState?: string | null;
  packActive?: boolean;
  packReady?: boolean;
  packCooldownSeconds?: number;
  packEnergy?: number | null;
  grenadeSlot?: number | null;
  grenadeId?: string | null;
  grenadeLabel?: string | null;
  grenadeType?: string | null;
  grenadeState?: string | null;
  grenadeActive?: boolean | null;
  grenadeReady?: boolean | null;
  grenadeCooldownSeconds?: number | null;
  grenadeCharges?: number | null;
  grenadeMaxCharges?: number | null;
};

type HudScoreboardPlayerSnapshot = {
  id?: string;
  displayName?: string;
  team?: string | null;
  isBot?: boolean;
  score?: number;
  kills?: number;
  deaths?: number;
  captures?: number;
  returns?: number;
};

type HudFlagSnapshot = {
  team?: string | null;
  position?: Vec3Like;
  homePosition?: Vec3Like;
  holderId?: string | null;
  state?: string | null;
  returnTimerSeconds?: number | null;
};

type HudBoostPadSnapshot = {
  id?: string;
  position?: Vec3Like;
  radius?: number;
  active?: boolean;
  cooldownSeconds?: number | null;
};

type HudBaseEntitySnapshot = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  radius?: number | null;
  active?: boolean | null;
  powered?: boolean | null;
  health?: number | null;
  maxHealth?: number | null;
  networkState?: 'online' | 'sensor-offline' | 'power-offline' | null;
  respawnSeconds?: number | null;
};

type HudVehicleSnapshot = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  homePosition?: Vec3Like;
  velocity?: Vec3Like;
  active?: boolean | null;
  vehicleId?: string | null;
  occupiedBy?: string | null;
  health?: number | null;
  maxHealth?: number | null;
  returnSeconds?: number | null;
  respawnSeconds?: number | null;
  weaponCooldown?: number | null;
  weaponId?: string | null;
  ammo?: number | null;
  // Phase 178 — flight/afterburner readout for air vehicles.
  boostActive?: boolean | null;
  boostCooldownSeconds?: number | null;
};

type HudDeployableSnapshot = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  active?: boolean | null;
  deployableId?: string | null;
  ttlSeconds?: number | null;
};

type HudSensorSnapshot = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  radius?: number | null;
  active?: boolean | null;
  health?: number | null;
  maxHealth?: number | null;
  respawnSeconds?: number | null;
};

type HudShieldDoorSnapshot = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  radius?: number | null;
  active?: boolean | null;
  powered?: boolean | null;
};

type HudMatchSnapshot = {
  phase?: string | null;
  scoreToWin?: number;
  teamScores?: Record<string, number> | null;
  leaderName?: string | null;
  winnerTeam?: string | null;
  countdownSeconds?: number | null;
  remainingSeconds?: number | null;
};

type HudEvent = {
  id?: string;
  message?: string;
  category?: string | null;
  priority?: number | null;
  combat?: CombatEvent;
};

  type HudEffectSnapshot = {
  id?: string;
  type?: string;
  ttlSeconds?: number;
  team?: string | null;
  weaponId?: string | null;
  sourcePlayerId?: string | null;
  targetPlayerId?: string | null;
  amount?: number | null;
  targetPosition?: Vec3Like;
  hitKind?: string | null;
  damageType?: 'explosive' | 'hitscan' | 'energy' | null;
  headshot?: boolean | null;
};

type HudDirective = {
  id?: string | null;
  team?: string | null;
  call?: string | null;
  message?: string | null;
  ttlSeconds?: number;
  sourceName?: string | null;
};

type HudTacticalPing = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  ttlSeconds?: number;
  kind?: string | null;
  label?: string | null;
};

type HudRouteCaptureZone = {
  id?: string;
  position?: Vec3Like;
  radius?: number;
  ownerTeam?: string | null;
  captureTargetTeam?: string | null;
  captureProgress?: number;
  contested?: boolean;
  carrierInside?: boolean;
  scoringMultiplier?: number;
};

type HudObjectiveScoringHint = {
  id?: string;
  team?: string | null;
  kind?: string | null;
  message?: string | null;
  points?: number;
  multiplier?: number;
  ttlSeconds?: number;
  flagTeam?: string | null;
  routeZoneId?: string | null;
};

type HudBaseFlagHook = {
  id?: string;
  team?: string | null;
  kind?: string | null;
  status?: 'stable' | 'warning' | 'critical';
  detail?: string;
  intensity?: number;
  active?: boolean;
};

type HudMissionSignal = {
  title: string;
  status: string;
  tone: string;
  urgency: 'normal' | 'critical' | 'warning';
  ttlSeconds?: number;
};

function colorModeTokens(mode: CameraSettings['colorMode']) {
  return resolveHudColorModeTokens(mode);
}

type HudRoutePressure = {
  routeText: string;
  contestedText: string;
  pressureText: string;
  cueText: string;
  tone: string;
};

type MovementTelemetrySnapshot = {
  frame: number;
  timestampMs: number;
  speed: number;
  grounded: boolean;
  jetPhase: 'idle' | 'burst' | 'sustain' | 'recovery' | 'depleted';
  jetIntensity?: number;
  skiIntensity?: number;
  speedWakeIntensity?: number;
  landingImpactIntensity?: number;
  landingQuality?: number;
  movementCue?: MovementCue;
  energy: number;
  energyDelta: number;
  energyBurnPerSecond: number;
  slopeAngle: number;
  airtimeSeconds: number;
  verticalSpeed: number;
  traceRecording: boolean;
  traceFrameCount: number;
  armorClass: string | null;
  flowScore: number;
  flowGrade: 'S' | 'A' | 'B' | 'C';
  flowReason: string;
  textState: string;
};

const MOVEMENT_TELEMETRY_EVENT = 'tribes2:movement-telemetry';
const DEV_FEEL_TUNING_ENABLED = Boolean(
  ((import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV) ??
    (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production'),
);

function isMovementTelemetryEquivalent(a: MovementTelemetrySnapshot | null, b: MovementTelemetrySnapshot | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }

  return (
    a.textState === b.textState &&
    a.speed === b.speed &&
    a.jetPhase === b.jetPhase &&
    a.grounded === b.grounded &&
    a.airtimeSeconds === b.airtimeSeconds &&
    a.movementCue === b.movementCue &&
    a.flowGrade === b.flowGrade &&
    a.flowScore === b.flowScore &&
    a.armorClass === b.armorClass
  );
}

type HudStoreSnapshot = {
  connected?: boolean;
  connectionState?: 'connecting' | 'online' | 'reconnecting' | 'offline' | 'standalone';
  gamePhase?: 'menu' | 'loading' | 'playing' | 'paused' | 'postmatch';
  localPlayer?: HudPlayerSnapshot;
  remotePlayers?: Record<string, HudPlayerSnapshot>;
  scoreboardPlayers?: HudScoreboardPlayerSnapshot[];
  match?: HudMatchSnapshot;
  events?: HudEvent[];
  effects?: HudEffectSnapshot[];
  activeDirective?: HudDirective | null;
  tacticalPings?: HudTacticalPing[];
  // Phase 155 — command-map waypoint pings (server filters to recipient team).
  pingWaypoints?: Array<{
    id: string;
    team: string | null;
    position: { x: number; y: number; z: number };
    ttlSeconds: number;
    callerId?: string | null;
    callerName?: string | null;
    kind?: string | null;
    color?: string | null;
  }>;
  routeCaptureZones?: HudRouteCaptureZone[];
  objectiveScoringHints?: HudObjectiveScoringHint[];
  baseFlagInteractionHooks?: HudBaseFlagHook[];
  flags?: HudFlagSnapshot[];
  boostPads?: HudBoostPadSnapshot[];
  generators?: HudBaseEntitySnapshot[];
  sensors?: HudSensorSnapshot[];
  shieldDoors?: HudShieldDoorSnapshot[];
  inventoryStations?: HudBaseEntitySnapshot[];
  repairZones?: HudBaseEntitySnapshot[];
  turrets?: HudBaseEntitySnapshot[];
  vehicles?: HudVehicleSnapshot[];
  deployables?: HudDeployableSnapshot[];
  ctfMacro?: CtfMacroSummary | null;
};

function frameClip() {
  return 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))';
}

function movementTelemetryAccent(snapshot: MovementTelemetrySnapshot | null) {
  if (!snapshot) {
    return '#9fb4c7';
  }

  if (snapshot.traceRecording) {
    return '#b58cff';
  }

  if (snapshot.jetPhase === 'depleted') {
    return '#ff8d74';
  }

  if (snapshot.jetPhase === 'recovery') {
    return '#8ef0c9';
  }

  if (snapshot.jetPhase === 'burst') {
    return '#ffcc66';
  }

  if (snapshot.jetPhase === 'sustain') {
    return '#62ddff';
  }

  return snapshot.grounded ? '#62ddff' : '#ffcc66';
}

function movementTelemetryStatus(snapshot: MovementTelemetrySnapshot | null) {
  if (!snapshot) {
    return 'WAITING';
  }

  if (snapshot.traceRecording) {
    return `TRACE ${snapshot.traceFrameCount}`;
  }

  if (snapshot.jetPhase === 'burst') {
    return 'JET BURST';
  }

  if (snapshot.jetPhase === 'sustain') {
    return 'JET SUSTAIN';
  }

  if (snapshot.jetPhase === 'recovery') {
    return 'JET RECOVERY';
  }

  if (snapshot.jetPhase === 'depleted') {
    return 'JET DEPLETED';
  }

  return snapshot.grounded ? 'GROUND' : 'AIR';
}

function movementTelemetryDetail(snapshot: MovementTelemetrySnapshot | null) {
  if (!snapshot) {
    return 'No live movement sample yet';
  }

  return snapshot.textState;
}

function jetPhaseLabel(jetPhase: MovementTelemetrySnapshot['jetPhase']) {
  if (jetPhase === 'idle') {
    return 'IDLE';
  }

  return jetPhase.replace(/[_-]/g, ' ').toUpperCase();
}

function movementCueLabel(snapshot: MovementTelemetrySnapshot | null) {
  return snapshot?.movementCue ?? '--';
}

function flowTone(grade?: MovementTelemetrySnapshot['flowGrade']) {
  if (grade === 'S') return '#ffcc66';
  if (grade === 'A') return '#8ef0c9';
  if (grade === 'B') return '#62ddff';
  return '#9fb4c7';
}

function playAnnouncerTone(tone: AnnouncerTone) {
  if (typeof window === 'undefined') {
    return;
  }

  const maybeWindow = window as Window & { webkitAudioContext?: typeof AudioContext };
  const AudioContextCtor = window.AudioContext ?? maybeWindow.webkitAudioContext;
  if (!AudioContextCtor) {
    return;
  }

  try {
    const context = new AudioContextCtor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const profile = announcerToneProfile(tone);
    oscillator.type = tone === 'alert' ? 'sawtooth' : tone === 'success' ? 'triangle' : 'sine';
    oscillator.frequency.value = profile.frequency;
    oscillator.connect(gain);
    gain.connect(context.destination);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + profile.durationMs / 1000);
    oscillator.start();
    oscillator.stop(context.currentTime + profile.durationMs / 1000);
    oscillator.addEventListener('ended', () => {
      void context.close();
    });
  } catch {
    // Audio fallback is best-effort; silently ignore blocked contexts.
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function formatMetric(value?: number, unit = '', precision = 0) {
  if (value == null || !Number.isFinite(value)) {
    return '--';
  }

  const safe = Number(value.toFixed(precision));
  return `${safe}${unit}`;
}

function formatSeconds(value?: number | null, precision = 2) {
  if (value == null || !Number.isFinite(value)) {
    return '--';
  }

  return `${Math.max(0, value).toFixed(precision)}s`;
}

function toTeamText(team?: string | null) {
  return (team ?? '').toString().trim().toUpperCase() || 'NEUTRAL';
}

function teamTone(team?: string | null) {
  return resolveTeamAccentColor(team);
}

function normalizeTeamKey(team?: string | null) {
  return (team ?? '').toString().trim().toLowerCase();
}

function planarDistance(from?: Vec3Like, to?: Vec3Like) {
  if (!from || !to) {
    return Number.POSITIVE_INFINITY;
  }
  return Math.hypot((to.x ?? 0) - (from.x ?? 0), (to.z ?? 0) - (from.z ?? 0));
}

function vehicleLabel(vehicleId?: string | null) {
  return (vehicleId ?? 'vehicle')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toUpperCase();
}

type VehiclePadCue = {
  vehicleName: string;
  status: string;
  footer: string;
  tone: string;
};

function resolveNearestVehiclePadCue(
  localPlayer: HudPlayerSnapshot,
  vehicles: HudVehicleSnapshot[],
  generators: HudBaseEntitySnapshot[],
  occupiedVehicle: HudVehicleSnapshot | null,
): VehiclePadCue | null {
  if (occupiedVehicle || !localPlayer.position) {
    return null;
  }

  const localTeam = normalizeTeamKey(localPlayer.team);
  if (!localTeam) {
    return null;
  }

  const nearestFriendlyVehicle = vehicles
    .filter((vehicle) => normalizeTeamKey(vehicle.team) === localTeam && (vehicle.homePosition ?? vehicle.position))
    .map((vehicle) => {
      const padPosition = vehicle.homePosition ?? vehicle.position;
      const padDistance = planarDistance(localPlayer.position, padPosition);
      const vehicleDistance = planarDistance(localPlayer.position, vehicle.position);
      return { vehicle, padPosition, padDistance, vehicleDistance };
    })
    .sort((left, right) => left.padDistance - right.padDistance)[0];

  if (!nearestFriendlyVehicle) {
    return null;
  }

  const { vehicle, padPosition, padDistance, vehicleDistance } = nearestFriendlyVehicle;
  const generatorOnline = generators.find((generator) => normalizeTeamKey(generator.team) === localTeam)?.active !== false;
  const atPad = planarDistance(vehicle.position, padPosition) <= 8;
  const boardable = vehicle.active !== false && !vehicle.occupiedBy && vehicleDistance <= 10;
  const closeEnoughForHud = padDistance <= 72 || boardable || Number(vehicle.returnSeconds ?? 0) > 0;

  if (!closeEnoughForHud) {
    return null;
  }

  const padDistanceText = `${Math.max(0, Math.round(padDistance))}m`;

  if (!generatorOnline) {
    return {
      vehicleName: vehicleLabel(vehicle.vehicleId),
      status: 'GENERATOR OFFLINE',
      footer: `PAD ${padDistanceText}`,
      tone: '#ff8d74',
    };
  }

  if (vehicle.active === false) {
    return {
      vehicleName: vehicleLabel(vehicle.vehicleId),
      status: (vehicle.respawnSeconds ?? 0) > 0 ? `REBUILD ${Math.ceil(Number(vehicle.respawnSeconds))}S` : 'VEHICLE DISABLED',
      footer: `PAD ${padDistanceText}`,
      tone: '#ff8d74',
    };
  }

  if (vehicle.occupiedBy) {
    return {
      vehicleName: vehicleLabel(vehicle.vehicleId),
      status: 'VEHICLE OCCUPIED',
      footer: `PAD ${padDistanceText}`,
      tone: '#ffcc66',
    };
  }

  if ((vehicle.returnSeconds ?? 0) > 0) {
    return {
      vehicleName: vehicleLabel(vehicle.vehicleId),
      status: `RETURNING ${Math.ceil(Number(vehicle.returnSeconds))}S`,
      footer: `PAD ${padDistanceText}`,
      tone: '#b394ff',
    };
  }

  if (boardable) {
    return {
      vehicleName: vehicleLabel(vehicle.vehicleId),
      status: 'READY AT PAD',
      footer: 'PRESS V TO BOARD',
      tone: '#8ef0c9',
    };
  }

  if (atPad) {
    return {
      vehicleName: vehicleLabel(vehicle.vehicleId),
      status: 'READY AT PAD',
      footer: `PAD ${padDistanceText}`,
      tone: '#8ef0c9',
    };
  }

  return {
    vehicleName: vehicleLabel(vehicle.vehicleId),
    status: 'DEPLOYED IN FIELD',
    footer: `PAD ${padDistanceText}`,
    tone: '#ffcc66',
  };
}

function resolveMissionSignal(
  localPlayer: HudPlayerSnapshot,
  flags: HudFlagSnapshot[],
  activeDirective: HudDirective | null,
): HudMissionSignal {
  const localTeam = (localPlayer.team ?? null)?.toString().toLowerCase() ?? null;
  const localPlayerId = localPlayer.id;
  const carriedFlagTeam = localPlayer.carryingFlagTeam != null ? localPlayer.carryingFlagTeam.toString().toLowerCase() : null;
  const localCarrier = flags.find((flag) => flag.holderId != null && localPlayerId != null && flag.holderId === localPlayerId && localTeam != null && flag.team != null && flag.team.toLowerCase() !== localTeam);
  const localPlayerCarryingEnemyFlag = carriedFlagTeam != null && localTeam != null && carriedFlagTeam !== localTeam;
  const ourFlag = flags.find((flag) => flag.team != null && flag.team.toLowerCase() === localTeam);
  const mission = activeDirective
      ? {
          title: `${activeDirective.team ? toTeamText(activeDirective.team) : 'TEAM'} ${activeDirective.call?.toUpperCase() ?? 'ORDER'}`,
          status: activeDirective.message ?? activeDirective.call ?? 'Mission in effect',
          tone: teamTone(activeDirective.team?.toString() ?? localTeam),
          urgency: 'warning' as const,
          ttlSeconds: activeDirective.ttlSeconds,
        }
    : null;

  if (mission) {
    return mission;
  }

  if (localCarrier || localPlayerCarryingEnemyFlag) {
    return {
      title: 'CARRYING FLAG',
      status: 'Return to home base lane before interception',
      tone: '#ffcc66',
      urgency: 'warning' as const,
      ttlSeconds: 12,
    };
  }

  if (ourFlag?.holderId && ourFlag.holderId !== localPlayerId) {
    return {
      title: 'ENEMY CARRIER',
      status: `Enemy carrying ${toTeamText(ourFlag.team)} flag — defend your base lane`,
      tone: teamTone(ourFlag.team),
      urgency: 'critical' as const,
      ttlSeconds: 12,
    };
  }

  return {
    title: 'ROUTE CONTROL',
    status: 'Hold midline dominance and reset the lane pressure',
    tone: '#86a9ff',
    urgency: 'normal' as const,
    ttlSeconds: 8,
  };
}

function classifyWeaponRole(weaponId?: string | null) {
  const key = (weaponId ?? '').toLowerCase();
  if (/spinfusor|disc|burst|plasma|launcher|grenade/.test(key)) {
    return { tone: '#62ddff', role: 'burst' };
  }

  if (/chaingun|chain|gun|bullet|slug|minigun|auto|rifle/.test(key)) {
    return { tone: '#ffcc66', role: 'sustained' };
  }

  if (/repair|heal|support|boost|sensor|jam|tool/.test(key)) {
    return { tone: '#b58cff', role: 'utility' };
  }

  if (key) {
    const hash = key.split('').reduce((accumulator, character) => ((accumulator * 33 + character.charCodeAt(0)) >>> 0), 0);
    return {
      tone: ['#62ddff', '#ffcc66', '#b58cff'][hash % 3],
      role: 'generic',
    };
  }

  return { tone: '#9fb4c7', role: 'generic' };
}

function normalizeWeaponConfig(weaponId?: string | null) {
  const key = `${weaponId ?? ''}`.toLowerCase();
  if (key === 'spinfusor' || key === 'chaingun' || key === 'mortar' || key === 'blaster') {
    return WEAPONS[key];
  }

  return null;
}

function weaponTuningLabel(weaponId?: string | null) {
  const config = normalizeWeaponConfig(weaponId);
  if (!config) {
    return 'SPREAD --';
  }

  if (config.fireMode === 'hitscan') {
    return `AIM RADIUS ${formatMetric(config.hitscanRadius, 'm', 1)}`;
  }

  if (config.splashRadius != null) {
    return `SPLASH ${formatMetric(config.splashRadius, 'm', 0)}`;
  }

  return `PROJECTILE ${formatMetric(config.projectileRadius, 'm', 1)}`;
}

function DevFeelTuningMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  const accent = tone ?? '#62ddff';

  return (
    <div
      style={{
        display: 'grid',
        gap: 3,
        padding: '7px 8px',
        minWidth: 0,
        background: `${accent}10`,
        border: `1px solid ${accent}2e`,
        clipPath: frameClip(),
        WebkitClipPath: frameClip(),
      }}
    >
      <span style={{ fontSize: 8.5, letterSpacing: 1.25, textTransform: 'uppercase', color: `${accent}d8` }}>{label}</span>
      <strong style={{ fontSize: 12, lineHeight: 1.1, color: '#f4fbff', fontVariantNumeric: 'tabular-nums' }}>{value}</strong>
    </div>
  );
}

function DevFeelTuningPanel({
  telemetry,
  localPlayer,
  remotePlayers,
  combatCue,
  compact,
  hudScale,
}: {
  telemetry: MovementTelemetrySnapshot | null;
  localPlayer: HudPlayerSnapshot;
  remotePlayers: Record<string, HudPlayerSnapshot>;
  combatCue: HudCue | null;
  compact: boolean;
  hudScale: number;
}) {
  const weaponId = localPlayer.selectedWeapon ?? null;
  const weaponIdentity = getWeaponIdentity(weaponId);
  const weaponConfig = normalizeWeaponConfig(weaponId);
  const weaponDebug = localPlayer.weaponDebug ?? null;
  const weaponTone = weaponIdentity?.tone ?? classifyWeaponRole(weaponId).tone;
  const botSignals = Object.values(remotePlayers).filter((player) => player.isBot && player.botRole);
  const primaryBot = botSignals[0] ?? null;
  const botIntent = primaryBot?.botIntent ?? null;
  const speed = telemetry?.speed ?? localPlayer.speed;
  const grounded = telemetry?.grounded ?? localPlayer.isGrounded ?? false;
  const skiing = Boolean(localPlayer.skiing);
  const jetState = telemetry ? jetPhaseLabel(telemetry.jetPhase) : 'JET --';
  const energy = telemetry?.energy ?? localPlayer.energy;
  const energyBurn = telemetry ? `${telemetry.energyBurnPerSecond.toFixed(1)}/s` : '--';
  const cycle = weaponConfig?.cooldownSeconds ?? weaponIdentity?.cooldownWindow ?? null;
  const currentCooldown = Math.max(0, weaponDebug?.cooldownSeconds ?? localPlayer.weaponCooldown ?? 0);
  const heat = Math.max(0, weaponDebug?.heatPercent ?? localPlayer.weaponHeat ?? 0);
  const readiness = weaponDebug?.readiness;
  const recoilImpulse = weaponDebug?.recoilImpulse;
  const aimErrorDegrees = weaponDebug?.aimErrorDegrees;
  const lastShotResult = weaponDebug?.lastShotResult && weaponDebug.lastShotResult !== 'none' ? weaponDebug.lastShotResult : null;
  const lastShotAge = weaponDebug?.lastShotAgeSeconds;
  const botText = primaryBot
    ? `${primaryBot.displayName ?? primaryBot.id ?? 'Bot'} · ${(primaryBot.botRole ?? 'bot').toUpperCase()}`
    : botSignals.length
      ? `${botSignals.length} bot signals`
      : 'No bot role signal';
  const botIntentText = botIntent
    ? `${botIntent.tactic ?? botIntent.objective ?? 'intent'} · ${botIntent.targetKind ?? 'target'} ${botIntent.targetDistance != null ? `${botIntent.targetDistance.toFixed(0)}m` : ''}`
    : null;

  return (
    <div
      data-dev-feel-tuning="true"
      style={{
        position: 'absolute',
        left: compact ? 12 : 16,
        top: compact ? 64 : 72,
        width: compact ? 'min(352px, calc(100vw - 24px))' : 'min(382px, 29vw)',
        transform: `scale(${hudScale})`,
        transformOrigin: 'top left',
        zIndex: 86,
      }}
    >
      <CompactHudCard accent="#8ef0c9" padding="10px 11px">
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#8ef0c9', fontWeight: 800 }}>
              Feel Tuning
            </span>
            <span style={{ fontSize: 9, letterSpacing: 1.4, textTransform: 'uppercase', color: 'rgba(231,244,252,0.62)' }}>
              F9
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>
            <DevFeelTuningMetric label="Speed" value={formatMetric(speed, '', 1)} tone="#62ddff" />
            <DevFeelTuningMetric label="Ground" value={grounded ? 'GROUND' : 'AIR'} tone={grounded ? '#8ef0c9' : '#ffcc66'} />
            <DevFeelTuningMetric label="Ski" value={skiing ? 'SKI ON' : 'SKI OFF'} tone={skiing ? '#62ddff' : '#9fb4c7'} />
            <DevFeelTuningMetric label="Jet" value={jetState} tone={movementTelemetryAccent(telemetry)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
            <DevFeelTuningMetric label="Energy" value={formatMetric(energy, '', 0)} tone="#7fdfff" />
            <DevFeelTuningMetric label="Burn" value={energyBurn} tone="#b58cff" />
            <DevFeelTuningMetric label="Air" value={telemetry ? formatSeconds(telemetry.airtimeSeconds, 1) : '--'} tone="#ffcc66" />
          </div>

          <div
            style={{
              display: 'grid',
              gap: 6,
              padding: '8px 9px',
              background: `${weaponTone}10`,
              border: `1px solid ${weaponTone}32`,
              clipPath: frameClip(),
              WebkitClipPath: frameClip(),
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: `${weaponTone}d8` }}>
                Weapon
              </span>
              <strong style={{ fontSize: 12, letterSpacing: 0.8, textTransform: 'uppercase', color: '#f4fbff' }}>
                {(weaponIdentity?.label ?? weaponId ?? 'unarmed').toUpperCase()}
              </strong>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {[
                `COOLDOWN ${formatSeconds(currentCooldown).toUpperCase()}`,
                `CYCLE ${formatSeconds(cycle).toUpperCase()}`,
                readiness != null ? `READY ${Math.round(readiness)}%` : null,
                `HEAT ${Math.round(heat)}%`,
                recoilImpulse != null ? `RECOIL ${recoilImpulse.toFixed(2)}` : null,
                aimErrorDegrees != null ? `AIM ERR ${aimErrorDegrees.toFixed(1)}` : null,
                lastShotResult ? `LAST ${lastShotResult.toUpperCase()}${lastShotAge != null ? ` ${lastShotAge.toFixed(1)}S` : ''}` : null,
                weaponDebug?.spreadRadiusMeters != null
                  ? `RADIUS ${formatMetric(weaponDebug.spreadRadiusMeters, 'm', 1)}`
                  : weaponTuningLabel(weaponId),
              ].filter((value): value is string => Boolean(value)).map((value) => (
                <span
                  key={value}
                  style={{
                    padding: '3px 6px',
                    background: 'rgba(255,255,255,0.045)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    clipPath: frameClip(),
                    WebkitClipPath: frameClip(),
                    fontSize: 9,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    color: 'rgba(237,247,255,0.82)',
                  }}
                >
                  {value}
                </span>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gap: 5, fontSize: 10, lineHeight: 1.25, color: 'rgba(231,244,252,0.72)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ color: 'rgba(231,244,252,0.52)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Combat</span>
              <span style={{ color: combatCue?.meta.tone ?? '#9fb4c7', textAlign: 'right' }}>
                {combatCue?.message ?? 'No combat cue'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ color: 'rgba(231,244,252,0.52)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Bot</span>
              <span style={{ color: primaryBot ? teamTone(primaryBot.team) : '#9fb4c7', textAlign: 'right' }}>
                {botText}
              </span>
            </div>
            {botIntentText ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ color: 'rgba(231,244,252,0.52)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Intent</span>
                <span style={{ color: '#8ef0c9', textAlign: 'right' }}>
                  {botIntentText} · CONF {Math.round((botIntent?.confidence ?? 0) * 100)}%
                </span>
              </div>
            ) : null}
            <div style={{ color: 'rgba(231,244,252,0.54)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {telemetry?.textState ?? 'Awaiting movement sample'}
            </div>
          </div>
        </div>
      </CompactHudCard>
    </div>
  );
}

type HudCueMeta = {
  category: 'critical' | 'objective' | 'combat' | 'support' | 'system';
  label: string;
  tone: string;
};

type HudCue = {
  id: string;
  message: string;
  meta: HudCueMeta;
  source: 'event' | 'effect';
};

export function summarizeCombatEffect(effects: HudEffectSnapshot[], localPlayerId?: string | null): HudCue | null {
  const priority = (effect: HudEffectSnapshot) => {
    const type = (effect.type ?? '').toLowerCase();
    const hitKind = (effect.hitKind ?? '').toLowerCase();
    if (type === 'hitconfirm' && effect.headshot) return 700;
    if (type === 'hitconfirm' && hitKind === 'direct') return 620;
    if (type === 'hitconfirm') return 580;
    if (type === 'shieldhit') return 500;
    if (type === 'vehiclehit' || type === 'deployablehit') return 430;
    if (type === 'blockedshot') return 340;
    if (type === 'nearmiss') return 220;
    return 0;
  };

  const combatEffects = (Array.isArray(effects) ? effects : [])
    .filter((effect) => {
      const type = (effect.type ?? '').toLowerCase();
      return (
        type === 'hitconfirm' ||
        type === 'blockedshot' ||
        type === 'nearmiss' ||
        type === 'shieldhit' ||
        type === 'vehiclehit' ||
        type === 'deployablehit'
      ) && (!localPlayerId || effect.sourcePlayerId === localPlayerId);
    })
    .sort((left, right) => priority(right) - priority(left) || (right.ttlSeconds ?? 0) - (left.ttlSeconds ?? 0));

  const effect = combatEffects[0];
  if (!effect) {
    return null;
  }

  const type = (effect.type ?? '').toLowerCase();
  const roleTone = classifyWeaponRole(effect.weaponId ?? null).tone;
  const teamToneValue = effect.team ? teamTone(effect.team) : roleTone;

  if (type === 'hitconfirm') {
    const amount = typeof effect.amount === 'number' && Number.isFinite(effect.amount) ? Math.max(1, Math.round(effect.amount)) : null;
    const hitKind = (effect.hitKind ?? '').toLowerCase();
    const label = effect.headshot
      ? 'Headshot'
      : hitKind === 'direct'
        ? 'Direct'
        : hitKind === 'splash'
          ? 'Splash'
          : hitKind === 'selfsplash'
            ? 'Self'
            : hitKind === 'shielded'
              ? 'Shield'
              : effect.damageType === 'hitscan'
                ? 'Direct'
                : 'Hit';
    return {
      id: `effect:${effect.id ?? 'hitconfirm'}`,
      message: amount ? `${label} ${amount}` : `${label} confirmed`,
      meta: { category: 'combat', label, tone: teamToneValue },
      source: 'effect',
    };
  }

  if (type === 'blockedshot') {
    return {
      id: `effect:${effect.id ?? 'blockedshot'}`,
      message: 'Shot blocked',
      meta: { category: 'combat', label: 'Block', tone: teamToneValue },
      source: 'effect',
    };
  }

  if (type === 'nearmiss') {
    return {
      id: `effect:${effect.id ?? 'nearmiss'}`,
      message: 'Near miss',
      meta: { category: 'combat', label: 'Near miss', tone: '#ffcc66' },
      source: 'effect',
    };
  }

  if (type === 'shieldhit') {
    const amount = typeof effect.amount === 'number' && Number.isFinite(effect.amount) ? Math.max(1, Math.round(effect.amount)) : null;
    return {
      id: `effect:${effect.id ?? 'shieldhit'}`,
      message: amount ? `Shield hit ${amount}` : 'Shield hit',
      meta: { category: 'combat', label: 'Shield', tone: '#62ddff' },
      source: 'effect',
    };
  }

  if (type === 'vehiclehit') {
    return {
      id: `effect:${effect.id ?? 'vehiclehit'}`,
      message: 'Vehicle damage',
      meta: { category: 'combat', label: 'Vehicle', tone: '#ff8d74' },
      source: 'effect',
    };
  }

  if (type === 'deployablehit') {
    return {
      id: `effect:${effect.id ?? 'deployablehit'}`,
      message: 'Deployable hit',
      meta: { category: 'combat', label: 'Deployable', tone: '#8ef0c9' },
      source: 'effect',
    };
  }

  return null;
}

function hasCriticalBaseOutage(
  generators: HudBaseEntitySnapshot[],
  sensors: HudSensorSnapshot[],
  shieldDoors: HudShieldDoorSnapshot[],
  turrets: HudBaseEntitySnapshot[],
  localTeam?: string | null,
) {
  const team = typeof localTeam === 'string' ? localTeam.toLowerCase() : null;
  const belongsToLocalTeam = (value?: string | null) => !team || (value ?? '').toLowerCase() === team;
  const localGenerator = generators.find((generator) => belongsToLocalTeam(generator.team));
  const localSensors = sensors.filter((sensor) => belongsToLocalTeam(sensor.team));
  const localShieldDoors = shieldDoors.filter((shieldDoor) => belongsToLocalTeam(shieldDoor.team));
  const localTurrets = turrets.filter((turret) => belongsToLocalTeam(turret.team));

  return Boolean(
    (localGenerator && (!localGenerator.active || !localGenerator.powered || (localGenerator.health ?? 1) <= 45)) ||
      localSensors.some((sensor) => !sensor.active || (sensor.health ?? 1) <= 0) ||
      localShieldDoors.some((shieldDoor) => !shieldDoor.active || !shieldDoor.powered) ||
      localTurrets.some((turret) => !turret.active || turret.networkState === 'sensor-offline' || turret.networkState === 'power-offline'),
  );
}

function summarizeDirective(activeDirective?: HudDirective | null) {
  if (!activeDirective) {
    return null;
  }

  const ttlSeconds = Math.max(0, activeDirective.ttlSeconds ?? 0);
  const call = activeDirective.call ?? null;
  const teamLabel = activeDirective.team ? activeDirective.team.toUpperCase() : 'TEAM';
  const message = activeDirective.message && activeDirective.message.length > 0 ? activeDirective.message : call ? `${teamLabel} ${call.toUpperCase()}` : null;
  if (!message) {
    return null;
  }

  return {
    message,
    ttlSeconds,
    sourceName: activeDirective.sourceName ?? null,
  };
}

function summarizeMissionSignal(
  activeDirective: ReturnType<typeof summarizeDirective>,
  hints: HudObjectiveScoringHint[],
  hooks: HudBaseFlagHook[],
  localTeam?: string | null,
) {
  const topHint = hints
    .filter((hint) => !hint.team || !localTeam || hint.team === localTeam)
    .sort((left, right) => (right.ttlSeconds ?? 0) - (left.ttlSeconds ?? 0))[0];
  const topRiskHook = hooks
    .filter((hook) => !hook.team || !localTeam || hook.team === localTeam)
    .sort((left, right) => {
      const leftWeight = (left.status === 'critical' ? 3 : left.status === 'warning' ? 2 : 1) * (left.intensity ?? 0);
      const rightWeight = (right.status === 'critical' ? 3 : right.status === 'warning' ? 2 : 1) * (right.intensity ?? 0);
      return rightWeight - leftWeight;
    })[0];

  if (topRiskHook && (topRiskHook.status === 'critical' || topRiskHook.status === 'warning')) {
    return {
      title: topRiskHook.status === 'critical' ? 'BASE UNDER PRESSURE' : 'BASE ATTENTION REQUIRED',
      status: topRiskHook.detail ?? 'Defensive systems need support',
      tone: topRiskHook.status === 'critical' ? '#ff8d74' : '#ffcc66',
      urgency: topRiskHook.status,
      ttlSeconds: Math.max(0, (topRiskHook.intensity ?? 0) * 6),
    };
  }

  if (topHint?.message) {
    return {
      title: 'OBJECTIVE FLOW',
      status: topHint.message,
      tone: '#62ddff',
      urgency: 'warning' as const,
      ttlSeconds: Math.max(0, topHint.ttlSeconds ?? 0),
    };
  }

  if (activeDirective) {
    return {
      title: 'TEAM DIRECTIVE',
      status: activeDirective.message,
      tone: '#62ddff',
      urgency: 'warning' as const,
      ttlSeconds: activeDirective.ttlSeconds,
    };
  }

  return null;
}

function ctfMacroPressureText(macro: CtfMacroSummary) {
  if (macro.returnWindowSeconds != null) {
    return `RETURN ${Math.ceil(macro.returnWindowSeconds)}S`;
  }

  if (macro.standoffSeconds != null) {
    return `STANDOFF ${Math.ceil(macro.standoffSeconds)}S`;
  }

  return macro.title;
}

function summarizeRoutePressure(
  routeZones: HudRouteCaptureZone[],
  hints: HudObjectiveScoringHint[],
  hooks: HudBaseFlagHook[],
  localTeam?: string | null,
  ctfMacro?: CtfMacroSummary | null,
): HudRoutePressure | null {
  if (!routeZones.length && !hints.length && !hooks.length && !ctfMacro) {
    return null;
  }

  const normalizedLocalTeam = (localTeam ?? '').toLowerCase();
  const ownedRoutes = routeZones.filter(
    (zone) => normalizedLocalTeam && (zone.ownerTeam ?? '').toLowerCase() === normalizedLocalTeam,
  ).length;
  const contestedRoutes = routeZones.filter((zone) => Boolean(zone.contested)).length;
  const contestedTarget = routeZones
    .filter((zone) => Boolean(zone.contested))
    .sort((left, right) => (right.captureProgress ?? 0) - (left.captureProgress ?? 0))[0];
  const topHook = hooks
    .filter((hook) => !hook.team || !normalizedLocalTeam || (hook.team ?? '').toLowerCase() === normalizedLocalTeam)
    .sort((left, right) => (right.intensity ?? 0) - (left.intensity ?? 0))[0];
  const topHint = hints
    .filter((hint) => !hint.team || !normalizedLocalTeam || (hint.team ?? '').toLowerCase() === normalizedLocalTeam)
    .sort((left, right) => (right.ttlSeconds ?? 0) - (left.ttlSeconds ?? 0))[0];

  const hookStatus = topHook?.status ?? 'stable';
  const macroPressureText = ctfMacro ? ctfMacroPressureText(ctfMacro) : null;
  const tone =
    ctfMacro?.tone ??
    (hookStatus === 'critical' ? '#ff8d74' : hookStatus === 'warning' ? '#ffcc66' : '#62ddff');
  const pressureText =
    macroPressureText ??
    (hookStatus === 'critical'
      ? 'Pressure CRITICAL'
      : hookStatus === 'warning'
        ? 'Pressure HIGH'
        : contestedRoutes > 0
          ? 'Pressure CONTESTED'
          : 'Pressure STABLE');
  const cueText =
    ctfMacro?.cue ??
    (contestedTarget?.id ? `Contest ${routeZoneLabel(contestedTarget.id)}` : null) ??
    topHint?.message ??
    topHook?.detail ??
    'Hold route velocity and deny enemy lanes';

  return {
    routeText: `${ownedRoutes}/${routeZones.length || 0}`,
    contestedText: `Contested ${contestedRoutes}`,
    pressureText,
    cueText,
    tone,
  };
}

function MotionMetric({
  label,
  value,
  tone,
  detail,
}: {
  label: string;
  value: string;
  tone: string;
  detail?: string;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 3,
        minWidth: 0,
        padding: '7px 9px',
        background: `${tone}10`,
        border: `1px solid ${tone}28`,
        clipPath: frameClip(),
        WebkitClipPath: frameClip(),
      }}
    >
      <span style={{ fontSize: 9, letterSpacing: 1.15, textTransform: 'uppercase', color: `${tone}c8` }}>{label}</span>
      <strong style={{ fontSize: 13, lineHeight: 1.05, color: '#f4fbff' }}>{value}</strong>
      {detail ? <span style={{ fontSize: 9, color: 'rgba(231,244,252,0.62)' }}>{detail}</span> : null}
    </div>
  );
}

function MovementTelemetryStrip({
  telemetry,
  compact,
  minimal = false,
}: {
  telemetry: MovementTelemetrySnapshot | null;
  compact: boolean;
  minimal?: boolean;
}) {
  const accent = movementTelemetryAccent(telemetry);
  const status = movementTelemetryStatus(telemetry);

  if (minimal) {
    return (
      <CompactHudCard accent={accent} padding="8px 10px">
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: accent }}>
              Movement
            </span>
            <span style={{ fontSize: 9.5, letterSpacing: 1.1, textTransform: 'uppercase', color: 'rgba(231,244,252,0.65)' }}>
              {status}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[
              telemetry ? `${telemetry.speed.toFixed(0)} m/s` : '-- m/s',
              telemetry ? `FLOW ${telemetry.flowGrade}${telemetry.flowScore}` : 'FLOW --',
              telemetry ? movementCueLabel(telemetry) : 'CUE --',
              telemetry ? `E${Math.round(telemetry.energy)}` : 'E--',
            ].map((value) => (
              <span
                key={value}
                style={{
                  padding: '3px 7px',
                  background: `${accent}12`,
                  border: `1px solid ${accent}28`,
                  clipPath: frameClip(),
                  WebkitClipPath: frameClip(),
                  fontSize: 9,
                  letterSpacing: 1.1,
                  textTransform: 'uppercase',
                  color: accent,
                }}
              >
                {value}
              </span>
            ))}
          </div>
        </div>
      </CompactHudCard>
    );
  }

  return (
    <CompactHudCard accent={accent} padding={compact ? '8px 10px' : '9px 11px'}>
      <div style={{ display: 'grid', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: accent }}>
            Movement
          </span>
          <span style={{ fontSize: 9.5, letterSpacing: 1.1, textTransform: 'uppercase', color: 'rgba(231,244,252,0.65)' }}>
            {status}
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: compact ? 'repeat(2, minmax(0, 1fr))' : 'repeat(5, minmax(0, 1fr))',
            gap: 6,
          }}
        >
          <MotionMetric
            label="Cue"
            value={movementCueLabel(telemetry)}
            tone={accent}
            detail={telemetry ? 'movement read' : 'awaiting sample'}
          />
          <MotionMetric
            label="Speed"
            value={telemetry ? `${telemetry.speed.toFixed(1)}m/s` : '--'}
            tone="#62ddff"
            detail={telemetry ? (telemetry.grounded ? 'ground' : 'air') : 'awaiting sample'}
          />
          <MotionMetric
            label="Flow"
            value={telemetry ? `${telemetry.flowGrade}${telemetry.flowScore}` : '--'}
            tone={flowTone(telemetry?.flowGrade)}
            detail={telemetry ? telemetry.flowReason : 'reward state'}
          />
          <MotionMetric
            label="Energy"
            value={telemetry ? `${Math.round(telemetry.energy)}` : '--'}
            tone="#7fdfff"
            detail={
              telemetry
                ? `${telemetry.energyDelta >= 0 ? '+' : ''}${telemetry.energyDelta.toFixed(1)} / ${telemetry.energyBurnPerSecond.toFixed(1)}s · ${jetPhaseLabel(telemetry.jetPhase)}`
                : 'burn rate'
            }
          />
          <MotionMetric
            label="Jet"
            value={telemetry ? jetPhaseLabel(telemetry.jetPhase) : '--'}
            tone={telemetry ? movementTelemetryAccent(telemetry) : '#b58cff'}
            detail={telemetry ? `${telemetry.airtimeSeconds.toFixed(1)}s air` : 'jet state'}
          />
        </div>
        {telemetry ? (
          <div style={{ fontSize: 9.5, lineHeight: 1.15, color: 'rgba(231,244,252,0.66)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {movementTelemetryDetail(telemetry)}
          </div>
        ) : null}
      </div>
    </CompactHudCard>
  );
}

function formatClock(totalSeconds?: number | null) {
  if (!Number.isFinite(totalSeconds ?? NaN)) {
    return '--:--';
  }

  const value = Math.max(0, Math.floor(totalSeconds ?? 0));
  const minutes = Math.floor(value / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (value % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function getTeamScore(teamScores: Record<string, number> | null | undefined, team: 'red' | 'blue') {
  return typeof teamScores?.[team] === 'number' ? teamScores[team] : 0;
}

function normalizeTeamForScoreboard(team?: string | null) {
  if (!team) {
    return null;
  }

  const normalized = team.toLowerCase();
  return normalized === 'red' || normalized === 'blue' ? normalized : null;
}

function shortPhase(phase?: string | null) {
  if (!phase) {
    return 'STANDBY';
  }

  return phase.replace(/[_-]/g, ' ').toUpperCase();
}

function compactFlagStatus(flag: HudFlagSnapshot | undefined) {
  if (!flag) {
    return 'HOME';
  }

  if (flag.holderId) {
    return 'TAKEN';
  }

  if (typeof flag.returnTimerSeconds === 'number' && flag.returnTimerSeconds > 0) {
    return `RETURN ${Math.ceil(flag.returnTimerSeconds)}S`;
  }

  const state = (flag.state ?? '').replace(/[_-]/g, ' ').trim();
  return state ? state.toUpperCase() : 'HOME';
}

function urgencyAccent(urgency: HudMissionSignal['urgency']) {
  if (urgency === 'critical') {
    return '#ff8d74';
  }

  if (urgency === 'warning') {
    return '#ffcc66';
  }

  return '#62ddff';
}

function softPanelStyle(accent: string, padding = '10px 12px'): CSSProperties {
  return {
    position: 'relative',
    overflow: 'hidden',
    padding,
    background: 'linear-gradient(180deg, rgba(5, 10, 17, 0.88), rgba(4, 8, 14, 0.78))',
    border: `1px solid rgba(155, 185, 212, 0.18)`,
    boxShadow: `inset 0 0 0 1px ${accent}10, 0 0 20px ${accent}08, 0 10px 30px rgba(0,0,0,0.22)`,
    color: '#edf7ff',
    clipPath: frameClip(),
    WebkitClipPath: frameClip(),
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  };
}

function CompactHudCard({
  accent,
  children,
  style,
  padding,
}: {
  accent: string;
  children: ReactNode;
  style?: CSSProperties;
  padding?: string;
}) {
  return <section style={{ ...softPanelStyle(accent, padding), ...style }}>{children}</section>;
}

function ScoreCell({
  team,
  score,
  compact = false,
}: {
  team: 'red' | 'blue';
  score: number;
  compact?: boolean;
}) {
  const accent = teamTone(team);

  return (
    <div
      style={{
        display: 'grid',
        gap: 3,
        alignContent: 'center',
        padding: compact ? '8px 8px 7px' : '8px 9px 7px',
        background: `${accent}12`,
        border: `1px solid ${accent}32`,
        clipPath: frameClip(),
        WebkitClipPath: frameClip(),
        textAlign: team === 'red' ? 'left' : 'right',
      }}
    >
      <span style={{ fontSize: compact ? 9 : 10, letterSpacing: 1.8, textTransform: 'uppercase', color: accent }}>{team}</span>
      <strong style={{ fontSize: compact ? 28 : 30, lineHeight: 0.95, fontVariantNumeric: 'tabular-nums' }}>{score}</strong>
    </div>
  );
}

function MeterRow({
  label,
  value,
  percent,
  tone,
}: {
  label: string;
  value: string;
  percent: number;
  tone: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          fontSize: 9.5,
          letterSpacing: 1.3,
          textTransform: 'uppercase',
        }}
      >
        <span style={{ color: 'rgba(231,244,252,0.62)' }}>{label}</span>
        <span style={{ color: '#edf7ff' }}>{value}</span>
      </div>
      <div
        aria-hidden="true"
        style={{
          height: 5,
          background: 'rgba(255,255,255,0.08)',
          overflow: 'hidden',
          borderRadius: 999,
        }}
      >
        <div
          style={{
            width: `${clamp(percent, 0, 100)}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${tone}, rgba(255,255,255,0.9))`,
            boxShadow: `0 0 12px ${tone}40`,
            transition: 'width 0.3s ease-out',
          }}
        />
      </div>
    </div>
  );
}

function SegmentedEnergyBar({ energy }: { energy?: number | null }) {
  // Phase 67 — prominent T2 energy bar: bigger segments, glowing blue gradient, bold readout
  const value = Math.max(0, Math.min(100, energy ?? 0));
  const filled = Math.max(0, Math.min(10, Math.ceil(value / 10)));
  const low = value < 25;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ display: 'flex', gap: 3 }}>
        {Array.from({ length: 10 }).map((_, i) => {
          const isOn = i < filled;
          const isLast = i === filled - 1;
          // Gradient from deeper blue (#1e7fff) at low to bright cyan (#7fdfff) at full
          const segColor = isOn
            ? (i < 3 ? '#1e7fff' : i < 7 ? '#3aa7ff' : '#7fdfff')
            : 'rgba(255,255,255,0.10)';
          return (
            <div
              key={i}
              style={{
                width: 14,
                height: 20,
                background: segColor,
                boxShadow: isOn
                  ? `0 0 ${isLast ? 12 : 8}px ${segColor}88, inset 0 0 4px rgba(255,255,255,0.25)`
                  : 'inset 0 0 2px rgba(0,0,0,0.5)',
                border: `1px solid ${isOn ? 'rgba(127,223,255,0.45)' : 'rgba(255,255,255,0.06)'}`,
                clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 3px), calc(100% - 3px) 100%, 0 100%)',
                WebkitClipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 3px), calc(100% - 3px) 100%, 0 100%)',
                transition: 'all 0.15s ease-out',
              }}
            />
          );
        })}
      </div>
      <span style={{
        fontSize: 16,
        fontWeight: 800,
        letterSpacing: 1.2,
        color: low ? '#ff7733' : '#aef0ff',
        fontVariantNumeric: 'tabular-nums',
        textShadow: low ? '0 0 8px #ff773366' : '0 0 8px #7fdfff66',
        minWidth: 32,
        textAlign: 'right',
      }}>
        {Math.round(value)}
      </span>
    </div>
  );
}

// Phase 130 — Pack-energy meter shown beneath the JET bar. Shows a small wide bar
// (5 segments) with pack label, glow ring when active, and yellow tint while on cooldown.
function PackEnergyMeter({
  packId,
  packEnergy,
  packActiveSeconds,
  packCooldown,
}: {
  packId: string | null;
  packEnergy: number;
  packActiveSeconds: number;
  packCooldown: number;
}) {
  if (!packId) {
    return null;
  }
  const value = Math.max(0, Math.min(100, packEnergy));
  const filled = Math.max(0, Math.min(5, Math.ceil(value / 20)));
  const isActive = packActiveSeconds > 0;
  const onCooldown = !isActive && packCooldown > 0;
  const tone = isActive ? '#ffd966' : onCooldown ? '#ff8d74' : '#7fdfff';
  const label = packId.toUpperCase();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 10px',
        background: 'linear-gradient(180deg, rgba(8,18,32,0.6), rgba(4,10,20,0.6))',
        border: `1px solid ${isActive ? tone : 'rgba(127,223,255,0.18)'}`,
        boxShadow: isActive ? `0 0 12px ${tone}88` : 'none',
        clipPath: 'polygon(0 4px, 4px 0, calc(100% - 4px) 0, 100% 4px, 100% calc(100% - 4px), calc(100% - 4px) 100%, 4px 100%, 0 calc(100% - 4px))',
        WebkitClipPath: 'polygon(0 4px, 4px 0, calc(100% - 4px) 0, 100% 4px, 100% calc(100% - 4px), calc(100% - 4px) 100%, 4px 100%, 0 calc(100% - 4px))',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      }}
    >
      <span
        style={{
          fontSize: 9,
          letterSpacing: 1.6,
          textTransform: 'uppercase',
          color: tone,
          fontWeight: 800,
          minWidth: 46,
          textAlign: 'left',
        }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', gap: 2 }}>
        {Array.from({ length: 5 }).map((_, i) => {
          const on = i < filled;
          return (
            <div
              key={i}
              style={{
                width: 14,
                height: 8,
                background: on ? tone : 'rgba(255,255,255,0.08)',
                boxShadow: on ? `0 0 4px ${tone}77` : 'inset 0 0 1px rgba(0,0,0,0.4)',
                border: `1px solid ${on ? `${tone}66` : 'rgba(255,255,255,0.05)'}`,
                transition: 'background 0.15s ease',
              }}
            />
          );
        })}
      </div>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: tone,
          fontVariantNumeric: 'tabular-nums',
          minWidth: 24,
          textAlign: 'right',
        }}
      >
        {onCooldown ? `${packCooldown.toFixed(1)}s` : Math.round(value)}
      </span>
    </div>
  );
}

function formatDebugVec3(value: { x: number; y: number; z: number } | undefined) {
  if (!value) {
    return '0.0, 0.0, 0.0';
  }
  return `${value.x.toFixed(1)}, ${value.y.toFixed(1)}, ${value.z.toFixed(1)}`;
}

function DebugReplayOverlay({
  summary,
  latest,
  compact,
  hudScale,
}: {
  summary: DebugTimelineSummary | null;
  latest: DebugTimelineFrame | null;
  compact: boolean;
  hudScale: number;
}) {
  const speed = latest ? Math.sqrt(latest.vel.x * latest.vel.x + latest.vel.y * latest.vel.y + latest.vel.z * latest.vel.z) : 0;
  return (
    <div
      style={{
        position: 'absolute',
        right: compact ? 12 : 16,
        top: compact ? 96 : 112,
        width: compact ? 'min(250px, calc(100vw - 24px))' : 280,
        transform: `scale(${hudScale})`,
        transformOrigin: 'top right',
        zIndex: 60,
      }}
    >
      <CompactHudCard accent="#b58cff" padding="9px 11px">
        <div style={{ display: 'grid', gap: 6, fontVariantNumeric: 'tabular-nums' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 9.5, letterSpacing: 1.6, textTransform: 'uppercase', color: '#d8c7ff', fontWeight: 800 }}>
              Debug Replay
            </span>
            <span style={{ fontSize: 9.5, color: 'rgba(237,247,255,0.7)' }}>
              {summary?.frameCount ?? 0}/{summary?.maxFrames ?? 600}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, fontSize: 10.5, lineHeight: 1.25 }}>
            <span style={{ color: 'rgba(237,247,255,0.66)' }}>tick</span>
            <span style={{ textAlign: 'right' }}>{latest?.tick ?? '--'}</span>
            <span style={{ color: 'rgba(237,247,255,0.66)' }}>map</span>
            <span style={{ textAlign: 'right' }}>{latest?.map ?? '--'}</span>
            <span style={{ color: 'rgba(237,247,255,0.66)' }}>speed</span>
            <span style={{ textAlign: 'right' }}>{speed.toFixed(1)}</span>
            <span style={{ color: 'rgba(237,247,255,0.66)' }}>pos</span>
            <span style={{ textAlign: 'right' }}>{formatDebugVec3(latest?.pos)}</span>
          </div>
          <div style={{ fontSize: 10, color: 'rgba(237,247,255,0.72)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {latest?.lastEvent ?? 'No replay event captured'}
          </div>
        </div>
      </CompactHudCard>
    </div>
  );
}

export function Hud() {
  const hud = useGameStore((state) => state as unknown as HudStoreSnapshot);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  const [movementTelemetry, setMovementTelemetry] = useState<MovementTelemetrySnapshot | null>(
    () => (typeof window !== 'undefined' ? window.tribes2MovementTelemetry ?? null : null),
  );
  const lastCueIdRef = useRef<string | null>(null);
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showCommandMap, setShowCommandMap] = useState(false);
  const [showFeelTuning, setShowFeelTuning] = useState(false);
  const [cameraSettings, setCameraSettings] = useState<CameraSettings>(() => readCameraSettings());
  const [coarsePointer, setCoarsePointer] = useState(false);
  const [damageFlash, setDamageFlash] = useState(false);
  const damageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastDamageEffectIdRef = useRef<string | null>(null);
  const [hitMarker, setHitMarker] = useState<{ active: boolean; color: string }>({ active: false, color: '#f4fbff' });
  const hitMarkerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastHitMarkerEffectIdRef = useRef<string | null>(null);
  const [killBanner, setKillBanner] = useState<{ active: boolean; name: string; weaponId?: string | null } | null>(null);
  const killBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastKillEventIdRef = useRef<string | null>(null);
  const [selfDamageFlash, setSelfDamageFlash] = useState(false);
  const selfDamageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSelfDamageEffectIdRef = useRef<string | null>(null);
  const [headshotFlash, setHeadshotFlash] = useState(false);
  const headshotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastHeadshotEffectIdRef = useRef<string | null>(null);
  const [debugReplayOverlay, setDebugReplayOverlay] = useState(false);
  const [debugReplayVersion, setDebugReplayVersion] = useState(0);
  const [tutorialProgress, setTutorialProgress] = useState(() => readTutorialProgress());
  const hasTriggeredPostmatchRef = useRef(false);
  const prevScoreRef = useRef(0);
  const scorePopupTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>());
  const [scorePopups, setScorePopups] = useState<Array<{ id: number; text: string }>>([]);
  const prevWeaponRef = useRef<string | null>(null);
  const [weaponSwapKey, setWeaponSwapKey] = useState(0);
  // Phase 97: when the local player takes damage, push the crosshair outward by ~6px for 80ms.
  const [crosshairFlick, setCrosshairFlick] = useState(0);
  const crosshairFlickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const telemetryEventFrameRef = useRef<MovementTelemetrySnapshot | null>(null);
  const telemetryEventFrameRafRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      for (const timer of scorePopupTimersRef.current) {
        clearTimeout(timer);
      }
      scorePopupTimersRef.current.clear();
    };
  }, []);
  useEffect(() => {
    const onDamage = () => {
      setCrosshairFlick(6);
      if (crosshairFlickTimerRef.current) clearTimeout(crosshairFlickTimerRef.current);
      crosshairFlickTimerRef.current = setTimeout(() => setCrosshairFlick(0), 80);
    };
    window.addEventListener('tribes2:damage-taken', onDamage as EventListener);
    return () => window.removeEventListener('tribes2:damage-taken', onDamage as EventListener);
  }, []);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        setShowScoreboard((prev) => !prev);
      } else if (DEV_FEEL_TUNING_ENABLED && e.key === 'F9') {
        e.preventDefault();
        setShowFeelTuning((prev) => !prev);
      } else if (e.key === 'Escape') {
        const phase = useGameStore.getState().gamePhase;
        if (phase !== 'playing' && phase !== 'paused' && !document.pointerLockElement) {
          setShowSettings((prev) => !prev);
        }
      } else if (e.key === 'F10' || e.key === '?' || (e.code === 'Slash' && e.shiftKey)) {
        e.preventDefault();
        setShowHelp((prev) => !prev);
      } else if ((e.key === 'm' || e.key === 'M') && !e.repeat) {
        // Phase 111 — Command map toggle. Ignore if typing in chat or other input.
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
        e.preventDefault();
        setShowCommandMap((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    return subscribeCameraSettings(setCameraSettings);
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }

    const media = window.matchMedia('(pointer: coarse), (any-pointer: coarse)');
    const sync = () => setCoarsePointer(media.matches);
    sync();
    media.addEventListener?.('change', sync);
    return () => media.removeEventListener?.('change', sync);
  }, []);

  useEffect(() => {
    const syncReplayOverlay = () => {
      const debugReplayVisible = Boolean(
        window.tribes2DebugOverlay ||
          window.tribes2DebugReplay ||
          (DEV_FEEL_TUNING_ENABLED && showFeelTuning),
      );

      setDebugReplayOverlay((current) => (current === debugReplayVisible ? current : debugReplayVisible));
      if (!debugReplayVisible) {
        setDebugReplayVersion(0);
        return;
      }

      const nextVersion = debugTimeline.getLatestFrame()?.t ?? 0;
      setDebugReplayVersion((current) => (current === nextVersion ? current : nextVersion));
    };

    syncReplayOverlay();
    const interval = window.setInterval(syncReplayOverlay, 250);
    return () => window.clearInterval(interval);
  }, [showFeelTuning]);

  const showDebugReplayOverlay = debugReplayOverlay || (DEV_FEEL_TUNING_ENABLED && showFeelTuning);
  const debugReplaySummary = useMemo(
    () => (showDebugReplayOverlay ? debugTimeline.getSummary() : null),
    [showDebugReplayOverlay, debugReplayVersion],
  );
  const debugReplayLatest = useMemo(
    () => (showDebugReplayOverlay ? debugTimeline.getLatestFrame() : null),
    [showDebugReplayOverlay, debugReplayVersion],
  );

  useEffect(() => {
    const syncTelemetry = (nextTelemetry: MovementTelemetrySnapshot | null) => {
      setMovementTelemetry((currentTelemetry) =>
        isMovementTelemetryEquivalent(currentTelemetry, nextTelemetry) ? currentTelemetry : nextTelemetry,
      );
    };

    const flushTelemetryEvent = () => {
      const snapshot = telemetryEventFrameRef.current;
      telemetryEventFrameRef.current = null;
      telemetryEventFrameRafRef.current = null;
      syncTelemetry(snapshot);
    };

    const scheduleTelemetrySync = (nextTelemetry: MovementTelemetrySnapshot | null) => {
      telemetryEventFrameRef.current = nextTelemetry;

      if (telemetryEventFrameRafRef.current !== null || typeof requestAnimationFrame !== 'function') {
        if (telemetryEventFrameRafRef.current === null) {
          syncTelemetry(nextTelemetry);
        }

        return;
      }

      telemetryEventFrameRafRef.current = requestAnimationFrame(flushTelemetryEvent);
    };

    const onTelemetryEvent = (event: Event) => {
      const snapshot = (event as CustomEvent<MovementTelemetrySnapshot | null>).detail ?? window.tribes2MovementTelemetry ?? null;
      scheduleTelemetrySync(snapshot);
    };

    scheduleTelemetrySync(window.tribes2MovementTelemetry ?? null);
    window.addEventListener(MOVEMENT_TELEMETRY_EVENT, onTelemetryEvent);

    return () => {
      window.removeEventListener(MOVEMENT_TELEMETRY_EVENT, onTelemetryEvent);
      if (telemetryEventFrameRafRef.current !== null) {
        cancelAnimationFrame(telemetryEventFrameRafRef.current);
        telemetryEventFrameRafRef.current = null;
      }
    };
  }, []);

  const compact = viewportWidth < 960;
  const narrowCompact = viewportWidth < 560;
  const compactUltra = viewportWidth < 660;
  const connected = hud.connected ?? false;
  const connectionState = hud.connectionState ?? (connected ? 'online' : 'offline');
  const gamePhase = hud.gamePhase ?? 'menu';
  const linkStatusLabel = connectionState === 'standalone' ? 'Local Sim' : connected ? 'Arena Online' : 'Link Lost';
  const linkStatusTone = connectionState === 'standalone' ? '#ffcc66' : connected ? '#62ddff' : '#ff8d74';
  const localPlayer = hud.localPlayer ?? {};
  const remotePlayers = hud.remotePlayers ?? {};
  const scoreboardPlayers = hud.scoreboardPlayers ?? [];
  const match = hud.match ?? {};
  const events = hud.events ?? [];
  const effects = hud.effects ?? [];

  useEffect(() => {
    const winner = match.winnerTeam;
    const phase = match.phase;
    if ((winner != null || phase === 'complete' || phase === 'postgame') && !hasTriggeredPostmatchRef.current) {
      hasTriggeredPostmatchRef.current = true;
      useGameStore.getState().setGamePhase('postmatch');
      document.exitPointerLock?.();
    }
    if (phase !== 'complete' && phase !== 'postgame' && winner == null) {
      hasTriggeredPostmatchRef.current = false;
      if (useGameStore.getState().gamePhase === 'postmatch') {
        useGameStore.getState().setGamePhase('playing');
      }
    }
  }, [match.winnerTeam, match.phase]);

  useEffect(() => {
    const damageEffect = effects.find((effect) => {
      const type = (effect.type ?? '').toLowerCase();
      return (
        (type === 'hitconfirm' || type === 'shieldhit') &&
        effect.targetPlayerId != null &&
        effect.targetPlayerId === localPlayer.id
      );
    });
    if (damageEffect && damageEffect.id && damageEffect.id !== lastDamageEffectIdRef.current) {
      lastDamageEffectIdRef.current = damageEffect.id;
      setDamageFlash(true);
      if (damageTimerRef.current) {
        clearTimeout(damageTimerRef.current);
      }
      damageTimerRef.current = setTimeout(() => {
        setDamageFlash(false);
      }, 400);
    }
  }, [effects, localPlayer.id]);

  useEffect(() => {
    const hitEffect = effects.find((effect) => {
      const type = (effect.type ?? '').toLowerCase();
      return (
        type === 'hitconfirm' &&
        effect.sourcePlayerId != null &&
        effect.sourcePlayerId === localPlayer.id
      );
    });
    if (hitEffect && hitEffect.id && hitEffect.id !== lastHitMarkerEffectIdRef.current) {
      lastHitMarkerEffectIdRef.current = hitEffect.id;
      const isEnemyHit = hitEffect.targetPlayerId != null && hitEffect.targetPlayerId !== localPlayer.id;
      const color = isEnemyHit ? '#ff4444' : '#f4fbff';
      setHitMarker({ active: true, color });
      if (hitMarkerTimerRef.current) {
        clearTimeout(hitMarkerTimerRef.current);
      }
      hitMarkerTimerRef.current = setTimeout(() => {
        setHitMarker({ active: false, color: '#f4fbff' });
      }, 150);
    }
  }, [effects, localPlayer.id]);

  useEffect(() => {
    const headshotEffect = effects.find((effect) => {
      const type = (effect.type ?? '').toLowerCase();
      return (
        type === 'hitconfirm' &&
        effect.sourcePlayerId != null &&
        effect.sourcePlayerId === localPlayer.id &&
        effect.headshot === true
      );
    });
    if (headshotEffect && headshotEffect.id && headshotEffect.id !== lastHeadshotEffectIdRef.current) {
      lastHeadshotEffectIdRef.current = headshotEffect.id;
      setHeadshotFlash(true);
      if (headshotTimerRef.current) {
        clearTimeout(headshotTimerRef.current);
      }
      headshotTimerRef.current = setTimeout(() => {
        setHeadshotFlash(false);
      }, 500);
    }
  }, [effects, localPlayer.id]);

  useEffect(() => {
    const selfDamageEffect = effects.find((effect) => {
      const type = (effect.type ?? '').toLowerCase();
      return (
        type === 'hitconfirm' &&
        effect.targetPlayerId != null &&
        effect.targetPlayerId === localPlayer.id &&
        effect.sourcePlayerId === localPlayer.id &&
        effect.hitKind === 'selfSplash'
      );
    });
    if (selfDamageEffect && selfDamageEffect.id && selfDamageEffect.id !== lastSelfDamageEffectIdRef.current) {
      lastSelfDamageEffectIdRef.current = selfDamageEffect.id;
      setSelfDamageFlash(true);
      if (selfDamageTimerRef.current) {
        clearTimeout(selfDamageTimerRef.current);
      }
      selfDamageTimerRef.current = setTimeout(() => {
        setSelfDamageFlash(false);
      }, 300);
    }
  }, [effects, localPlayer.id]);

  useEffect(() => {
    const killEvent = events.find((event) => {
      const category = (event.category ?? '').toLowerCase();
      const isKillCategory = category === 'killed' || category === 'kill';
      const isElimination = event.combat?.kind === 'elimination';
      const isLocalSource = event.combat?.attackerId === localPlayer.id;
      return (isKillCategory || isElimination) && isLocalSource;
    });
    if (killEvent && killEvent.id && killEvent.id !== lastKillEventIdRef.current) {
      lastKillEventIdRef.current = killEvent.id;
      const message = killEvent.message ?? '';
      const nameMatch = message.match(/eliminated\s+(.+?)(?:\s+with|\s*$)/i) || message.match(/killed\s+(.+?)(?:\s+with|\s*$)/i);
      const name = nameMatch ? nameMatch[1].trim() : 'TARGET';
      const weaponId = killEvent.combat?.weaponId ?? null;
      setKillBanner({ active: true, name, weaponId });
      if (killBannerTimerRef.current) {
        clearTimeout(killBannerTimerRef.current);
      }
      killBannerTimerRef.current = setTimeout(() => {
        setKillBanner(null);
      }, 2500);
    }
  }, [events, localPlayer.id]);

  const announcerCue = useMemo(
    () =>
      deriveAnnouncerCue(
        events.map((event) => ({
          id: event.id ?? null,
          message: event.message ?? null,
          category:
            event.category === 'critical' ||
            event.category === 'objective' ||
            event.category === 'combat' ||
            event.category === 'support' ||
            event.category === 'system'
              ? event.category
              : null,
          priority: typeof event.priority === 'number' ? event.priority : null,
        })),
    ),
    [events],
  );
  const activeDirective = summarizeDirective(hud.activeDirective ?? null);
  const tacticalPings = hud.tacticalPings ?? [];
  // Phase 155 — command-map ping waypoints (server filtered to local team).
  const pingWaypoints = hud.pingWaypoints ?? [];
  const routeCaptureZones = hud.routeCaptureZones ?? [];
  const objectiveScoringHints = hud.objectiveScoringHints ?? [];
  const baseFlagHooks = hud.baseFlagInteractionHooks ?? [];
  const ctfMacro = hud.ctfMacro ?? null;
  const flags = hud.flags ?? [];
  const boostPads = hud.boostPads ?? [];
  const generators = hud.generators ?? [];
  const sensors = hud.sensors ?? [];
  const shieldDoors = hud.shieldDoors ?? [];
  const inventoryStations = hud.inventoryStations ?? [];
  const repairZones = hud.repairZones ?? [];
  const turrets = hud.turrets ?? [];
  const vehicles = hud.vehicles ?? [];
  const deployables = hud.deployables ?? [];
  const occupiedVehicle = localPlayer.id
    ? vehicles.find((v) => v !== null && v.occupiedBy === localPlayer.id) ?? null
    : null;

  useEffect(() => {
    if (tutorialProgress.dismissed) {
      return;
    }

    const completed = new Set<TutorialStepId>(tutorialProgress.completedStepIds);
    const nextSteps: TutorialStepId[] = [];
    const mark = (stepId: TutorialStepId, complete: boolean) => {
      if (complete && !completed.has(stepId)) {
        completed.add(stepId);
        nextSteps.push(stepId);
      }
    };
    const localId = localPlayer.id ?? null;
    const eventText = events.map((event) => `${event.category ?? ''} ${event.message ?? ''} ${event.combat?.weaponId ?? ''}`).join(' ').toLowerCase();

    mark('move', (movementTelemetry?.speed ?? localPlayer.speed ?? 0) >= 4);
    mark('jet-ski', Boolean(localPlayer.skiing) || movementTelemetry?.jetPhase === 'burst' || movementTelemetry?.jetPhase === 'sustain' || (movementTelemetry?.airtimeSeconds ?? 0) > 0.4);
    mark('fire-weapon', effects.some((effect) => effect.sourcePlayerId === localId && (effect.type ?? '').toLowerCase() === 'hitconfirm') || events.some((event) => event.combat?.attackerId === localId) || eventText.includes('hit '));
    mark('throw-grenade', eventText.includes('grenade') || eventText.includes('frag') || eventText.includes('concussion'));
    mark('activate-pack', Boolean(localPlayer.packActive) || (localPlayer.packState ?? '').toLowerCase() === 'active');
    mark('deploy-mine', deployables.some((deployable) => deployable.team === localPlayer.team || (deployable.deployableId ?? '').toLowerCase().includes('mine')));
    mark('enter-vehicle', Boolean(occupiedVehicle));
    mark('objective', Boolean(localPlayer.carryingFlagTeam) || Boolean(ctfMacro) || Boolean(hud.activeDirective) || objectiveScoringHints.length > 0 || baseFlagHooks.some((hook) => hook.active));

    if (nextSteps.length === 0) {
      return;
    }

    let nextProgress = readTutorialProgress();
    for (const stepId of nextSteps) {
      nextProgress = completeTutorialStep(stepId);
    }
    setTutorialProgress(nextProgress);
  }, [
    baseFlagHooks,
    ctfMacro,
    deployables,
    effects,
    events,
    hud.activeDirective,
    localPlayer.carryingFlagTeam,
    localPlayer.id,
    localPlayer.packActive,
    localPlayer.packState,
    localPlayer.skiing,
    localPlayer.speed,
    localPlayer.team,
    movementTelemetry,
    objectiveScoringHints.length,
    occupiedVehicle,
    tutorialProgress.completedStepIds,
    tutorialProgress.dismissed,
  ]);

  useEffect(() => {
    const currentScore = localPlayer.score ?? 0;
    const prevScore = prevScoreRef.current;
    if (currentScore > prevScore) {
      const diff = Math.round(currentScore - prevScore);
      const popupId = Date.now() + Math.random();
      setScorePopups((prev) => [...prev.slice(-4), { id: popupId, text: `+${diff}` }]);
      const timeout = setTimeout(() => {
        setScorePopups((prev) => prev.filter((p) => p.id !== popupId));
        scorePopupTimersRef.current.delete(timeout);
      }, 1200);

      scorePopupTimersRef.current.add(timeout);
    }
    prevScoreRef.current = currentScore;
  }, [localPlayer.score]);

  useEffect(() => {
    const currentWeapon = localPlayer.selectedWeapon ?? null;
    if (prevWeaponRef.current != null && currentWeapon != null && prevWeaponRef.current !== currentWeapon) {
      setWeaponSwapKey((k) => k + 1);
    }
    prevWeaponRef.current = currentWeapon;
  }, [localPlayer.selectedWeapon]);

  const nearestVehiclePadCue = useMemo(
    () => resolveNearestVehiclePadCue(localPlayer, vehicles, generators, occupiedVehicle),
    [generators, localPlayer, occupiedVehicle, vehicles],
  );
  const adsActive = Boolean(localPlayer.ads);
  const speedMix = Math.min(1, (localPlayer.speed ?? 0) / 36);
  const reticleSize = adsActive
    ? 34 + (cameraSettings.reducedMotion ? 0 : speedMix) * 4
    : 54 + (cameraSettings.reducedMotion ? 0 : speedMix) * 12;
  const missionSignal =
    ctfMacro ??
    summarizeMissionSignal(activeDirective, objectiveScoringHints, baseFlagHooks, localPlayer.team ?? null) ??
    resolveMissionSignal(localPlayer, flags, hud.activeDirective ?? null);
  const routePressure = summarizeRoutePressure(
    routeCaptureZones,
    objectiveScoringHints,
    baseFlagHooks,
    localPlayer.team ?? null,
    ctfMacro,
  );
  const prioritizedEvents = useMemo(() => prioritizeHudEvents(events, 3), [events]);
  const latestCombatEvent = prioritizedEvents.find((event) => event.meta.category === 'combat') ?? null;
  const latestCombatEffect = useMemo(() => summarizeCombatEffect(effects, localPlayer.id), [effects, localPlayer.id]);
  const latestCombatEventCue = latestCombatEvent
    ? {
        id: latestCombatEvent.event.id ?? `event-${latestCombatEvent.index}`,
        message: latestCombatEvent.message,
        meta: latestCombatEvent.meta,
        source: 'event' as const,
      }
    : null;
  const latestGeneralEventCue = prioritizedEvents[0]
    ? {
        id: prioritizedEvents[0].event.id ?? `event-${prioritizedEvents[0].index}`,
        message: prioritizedEvents[0].message,
        meta: prioritizedEvents[0].meta,
        source: 'event' as const,
      }
    : null;
  const latestEvent = latestCombatEventCue ?? latestCombatEffect ?? latestGeneralEventCue;
  const highActionSpeed = (movementTelemetry?.speed ?? localPlayer.speed ?? 0) >= 52;
  const criticalMacro = ctfMacro?.urgency === 'critical' || ctfMacro?.role === 'returner' || ctfMacro?.role === 'carrier';
  const dynamicHudSuppressed =
    !showScoreboard &&
    !showSettings &&
    !showHelp &&
    (highActionSpeed || Boolean(localPlayer.carryingFlagTeam) || Boolean(latestCombatEventCue) || Boolean(latestCombatEffect) || criticalMacro);
  const hudMinimal = narrowCompact || dynamicHudSuppressed;
  const criticalBaseOutage = hasCriticalBaseOutage(generators, sensors, shieldDoors, turrets, localPlayer.team ?? null);
  const hudScale = clamp(cameraSettings.hudScale ?? 1, 0.7, 1.4);
  const mobileViewport = viewportWidth < 768;
  const effectiveScale = hudScale * (mobileViewport ? 0.85 : 1.0);
  const palette = colorModeTokens(cameraSettings.colorMode);
  const redScore = getTeamScore(match.teamScores, 'red');
  const blueScore = getTeamScore(match.teamScores, 'blue');
  const scoreTarget = match.scoreToWin ?? 0;
  const humanPlayers = scoreboardPlayers.filter((p) => !p.isBot);
  const displayRedCount = humanPlayers.length > 0
    ? humanPlayers.filter((p) => normalizeTeamForScoreboard(p.team) === 'red').length
    : 0;
  const displayBlueCount = humanPlayers.length > 0
    ? humanPlayers.filter((p) => normalizeTeamForScoreboard(p.team) === 'blue').length
    : 0;
  const teamsUnbalanced = displayRedCount > 0 && displayBlueCount > 0 && Math.abs(displayRedCount - displayBlueCount) >= 2;
  const redFlag = flags.find((flag) => (flag.team ?? '').toLowerCase() === 'red');
  const blueFlag = flags.find((flag) => (flag.team ?? '').toLowerCase() === 'blue');
  const objectiveAccent = urgencyAccent(missionSignal.urgency);
  const objectiveText = ctfMacro?.status ?? activeDirective?.message ?? missionSignal.status;
  const objectiveMeta = announcerCue?.label ?? `${shortPhase(match.phase)} · FIRST TO ${scoreTarget || '--'}`;
  const controlsText = compact ? 'WASD · Space jet · LMB fire · RMB aim' : 'F10 help · Tab score';
  const oobWarning = Boolean((localPlayer as Record<string, unknown>).oobWarning);
  const inWater = Boolean((localPlayer as Record<string, unknown>).inWater);
  const latestEventTone = latestEvent?.meta.tone ?? '#9fb4c7';
  const latestEventLabel = latestEvent?.meta.label?.toUpperCase() ?? 'SYSTEM';
  const latestEventText =
    latestEvent?.message ?? 'Routes stable. No new combat events.';
  const yawDeg = localPlayer.yaw != null ? ((-localPlayer.yaw * 180 / Math.PI) + 360) % 360 : 0;
  const compassLabel = (() => {
    const d = yawDeg;
    if (d >= 337.5 || d < 22.5) return 'N';
    if (d < 67.5) return 'NE';
    if (d < 112.5) return 'E';
    if (d < 157.5) return 'SE';
    if (d < 202.5) return 'S';
    if (d < 247.5) return 'SW';
    if (d < 292.5) return 'W';
    return 'NW';
  })();
  const currentTutorialStep = getCurrentTutorialStep(tutorialProgress);
  const tutorialAlign = 'left';
  const touchControlsVisible = cameraSettings.touchControlsEnabled || coarsePointer;
  const touchCompactHud = narrowCompact && touchControlsVisible;
  const narrowPanelMax = Math.max(132, Math.round(168 / Math.max(1, hudScale)));
  const narrowPanelVw = Math.max(38, Math.round(48 / Math.max(1, hudScale)));
  const bottomLeftWidth = narrowCompact
    ? `min(${narrowPanelMax}px, calc(${narrowPanelVw}vw - 14px))`
    : compact
      ? 'min(248px, calc(52vw - 20px))'
      : 'min(334px, 27vw)';
  const touchRadarMin = Math.max(104, Math.round(118 / Math.max(1, hudScale)));
  const touchRadarMax = Math.max(touchRadarMin, Math.round(136 / Math.max(1, hudScale)));
  const touchRadarVw = Math.max(32, Math.round(38 / Math.max(1, hudScale)));
  const radarMobileFactor = mobileViewport ? 0.7 : 1.0;
  const radarWidth = narrowCompact
    ? touchCompactHud
      ? `clamp(${touchRadarMin}px, calc(${touchRadarVw}vw - 12px), ${touchRadarMax}px)`
      : `min(${narrowPanelMax}px, calc(${narrowPanelVw}vw - 14px))`
    : compact
      ? `min(${Math.round(208 * radarMobileFactor)}px, calc(40vw - 18px))`
      : `min(${Math.round(236 * radarMobileFactor)}px, 21vw)`;
  const topStripWidth = narrowCompact
    ? 'calc(100vw - 24px)'
    : compact
      ? 'calc(100vw - 28px)'
      : 'clamp(520px, calc(100vw - 720px), 620px)';
  const compactTouchClearance = 188;
  const bottomDockOffset = touchCompactHud ? compactTouchClearance : narrowCompact ? 12 : 16;

  const handleSettingsChange = useCallback((next: CameraSettings) => {
    const normalized = writeCameraSettings(next);
    setCameraSettings(normalized);
  }, []);

  const handleSettingsClose = useCallback(() => {
    setShowSettings(false);
  }, []);

  let allPlayers: Array<{
    id: string;
    displayName: string;
    team: string | null;
    score: number;
    kills: number;
    deaths: number;
    captures: number;
    returns: number;
  }>;

  if (scoreboardPlayers.length) {
    allPlayers = scoreboardPlayers.map((player) => ({
      id: player.id ?? `scoreboard-${player.displayName ?? 'player'}`,
      displayName: player.displayName ?? 'Player',
      team: normalizeTeamForScoreboard(player.team),
      score: Math.round(player.score ?? 0),
      kills: player.kills ?? 0,
      deaths: player.deaths ?? 0,
      captures: player.captures ?? 0,
      returns: player.returns ?? 0,
    }));
  } else {
    allPlayers = [];
    if (localPlayer.id) {
      allPlayers.push({
        id: localPlayer.id,
        displayName: localPlayer.displayName ?? 'Player',
        team: normalizeTeamForScoreboard(localPlayer.team),
        score: Math.round(localPlayer.score ?? 0),
        kills: (localPlayer as Record<string, unknown>).kills as number ?? 0,
        deaths: (localPlayer as Record<string, unknown>).deaths as number ?? 0,
        captures: (localPlayer as Record<string, unknown>).captures as number ?? 0,
        returns: (localPlayer as Record<string, unknown>).returns as number ?? 0,
      });
    }
    for (const [playerId, player] of Object.entries(remotePlayers)) {
      allPlayers.push({
        id: player.id ?? playerId,
        displayName: player.displayName ?? `Player ${playerId}`,
        team: normalizeTeamForScoreboard(player.team),
        score: Math.round(player.score ?? 0),
        kills: (player as Record<string, unknown>).kills as number ?? 0,
        deaths: (player as Record<string, unknown>).deaths as number ?? 0,
        captures: (player as Record<string, unknown>).captures as number ?? 0,
        returns: (player as Record<string, unknown>).returns as number ?? 0,
      });
    }
  }

  useEffect(() => {
    if (!announcerCue || announcerCue.id === lastCueIdRef.current) {
      return;
    }
    lastCueIdRef.current = announcerCue.id;
    playAnnouncerTone(announcerCue.tone);
  }, [announcerCue]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        color: '#dcefff',
        ['--hud-scale' as string]: hudScale,
        ['--hud-accent' as string]: palette.accent,
        ['--hud-team-blue' as string]: palette.blue,
        ['--hud-team-red' as string]: palette.red,
      }}
    >
      <style>{`
        @keyframes fadeInEvent {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes damageVignette {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes hitMarkerExpand {
          from { transform: scale(1); opacity: 1; }
          to { transform: scale(1.4); opacity: 0; }
        }
        @keyframes killBannerFade {
          0% { opacity: 0; transform: translateY(-8px) scale(0.96); }
          6% { opacity: 1; transform: translateY(0) scale(1); }
          80% { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-4px) scale(0.98); }
        }
        @keyframes selfDamageVignette {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes oobPulse {
          0%, 100% { opacity: 0.25; }
          50% { opacity: 0.55; }
        }
        @keyframes oobTextFlash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes waterWave {
          0% { transform: translateY(0); }
          50% { transform: translateY(2px); }
          100% { transform: translateY(0); }
        }
        @keyframes hudSlideIn {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes healthPulse {
          from { opacity: 0.7; }
          to   { opacity: 1; }
        }
        @keyframes reticleBreathe {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.02); }
        }
        @keyframes reticleHitFlash {
          0% { border-color: #ff4444; box-shadow: 0 0 16px rgba(255,68,68,0.6); }
          100% { border-color: rgba(200,240,255,0.65); box-shadow: none; }
        }
        @keyframes reticleKillPulse {
          0% { transform: translate(-50%, -50%) scale(1); border-color: #ffffff; box-shadow: 0 0 24px rgba(255,255,255,0.8); }
          50% { transform: translate(-50%, -50%) scale(1.12); border-color: #ffffff; box-shadow: 0 0 32px rgba(255,255,255,0.9); }
          100% { transform: translate(-50%, -50%) scale(1); border-color: rgba(200,240,255,0.65); box-shadow: none; }
        }
        @keyframes lowHealthPulse {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.3; }
        }
        @keyframes killFeedSlideIn {
          from { transform: translateX(40px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes killFeedFadeOut {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
        @keyframes scoreFloatUp {
          0%   { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-40px); opacity: 0; }
        }
        @keyframes weaponSwapOut {
          from { transform: translateX(0); opacity: 1; }
          to   { transform: translateX(-20px); opacity: 0; }
        }
        @keyframes weaponSwapIn {
          from { transform: translateX(20px); opacity: 0; }
          to   { transform: translateX(0); opacity: 1; }
        }
        @keyframes hudScanline {
          0%   { top: -2px; }
          100% { top: 100%; }
        }
        @keyframes flagStolen {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1.0; }
        }
        @keyframes connBannerPulse {
          0%, 100% { opacity: 0.85; }
          50% { opacity: 1; }
        }
        @keyframes connDotBlink {
          0%, 20% { opacity: 0.3; }
          50% { opacity: 1; }
          80%, 100% { opacity: 0.3; }
        }
      `}</style>
      {/* Phase 116 — Team score ticker with faction palette + flag-status icons */}
      <div style={{
        position: 'absolute',
        top: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        pointerEvents: 'none',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 9, color: palette.blue, fontFamily: 'monospace', letterSpacing: 1.6, textTransform: 'uppercase' }}>
              BLUE {compactFlagStatus(blueFlag)}
            </span>
            <span style={{ fontSize: 16, color: palette.blue, fontFamily: 'monospace', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {blueScore}
            </span>
          </div>
          <div style={{
            width: Math.min(180, (blueScore / Math.max(1, scoreTarget)) * 180),
            height: 4,
            background: palette.blue,
            borderRadius: 2,
            transition: 'width 0.4s ease',
            minWidth: 2,
          }} />
        </div>
        <div style={{ color: '#ffffff88', fontSize: 10, fontFamily: 'monospace' }}>—</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 16, color: palette.red, fontFamily: 'monospace', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {redScore}
            </span>
            <span style={{ fontSize: 9, color: palette.red, fontFamily: 'monospace', letterSpacing: 1.6, textTransform: 'uppercase' }}>
              RED {compactFlagStatus(redFlag)}
            </span>
          </div>
          <div style={{
            width: Math.min(180, (redScore / Math.max(1, scoreTarget)) * 180),
            height: 4,
            background: palette.red,
            borderRadius: 2,
            transition: 'width 0.4s ease',
            minWidth: 2,
          }} />
        </div>
      </div>
      {!narrowCompact && !dynamicHudSuppressed ? (
        <div
          style={{
            position: 'absolute',
            left: compact ? 12 : 16,
            top: compact ? 12 : 16,
            width: compact ? 'min(208px, 28vw)' : 'min(184px, 16vw)',
            transform: `scale(${effectiveScale})`,
            transformOrigin: 'top left',
          }}
        >
          <CompactHudCard accent={linkStatusTone} padding={compact ? '8px 10px' : '9px 11px'}>
            <div style={{ display: 'grid', gap: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 9.5, letterSpacing: 1.6, textTransform: 'uppercase', color: linkStatusTone }}>
                  {linkStatusLabel}
                </span>
                <span style={{ fontSize: 9.5, color: 'rgba(231,244,252,0.65)' }}>{controlsText}</span>
              </div>
              {compact && !compactUltra ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {['1-4', 'WASD', 'SPACE', 'LMB', 'RMB', 'R', 'G'].map((key) => (
                    <span
                      key={key}
                      style={{
                        padding: '3px 6px',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        clipPath: frameClip(),
                        WebkitClipPath: frameClip(),
                        fontSize: 9,
                        letterSpacing: 1.1,
                        textTransform: 'uppercase',
                        color: '#f4fbff',
                      }}
                    >
                      {key}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </CompactHudCard>
        </div>
      ) : null}

      {(narrowCompact || dynamicHudSuppressed) && criticalBaseOutage && !touchCompactHud ? (
        <div
          style={{
            position: 'absolute',
            right: narrowCompact ? 12 : 16,
            top: narrowCompact ? 98 : compact ? 82 : 88,
            width: narrowCompact ? `min(${narrowPanelMax}px, calc(${narrowPanelVw}vw - 14px))` : compact ? 'min(214px, 31vw)' : 'min(214px, 19vw)',
            transform: `scale(${effectiveScale})`,
            transformOrigin: 'top right',
          }}
        >
          <BaseSystemsPanel
            localPlayer={localPlayer}
            generators={generators}
            sensors={sensors}
            shieldDoors={shieldDoors}
            inventoryStations={inventoryStations}
            repairZones={repairZones}
            turrets={turrets}
            compact
            minimal
          />
        </div>
      ) : null}

      {dynamicHudSuppressed && latestCombatEffect && !touchCompactHud ? (
        <div
          style={{
            position: 'absolute',
            right: narrowCompact ? 12 : 16,
            top: criticalBaseOutage ? (narrowCompact ? 228 : 218) : narrowCompact ? 98 : compact ? 82 : 88,
            width: narrowCompact ? `min(${narrowPanelMax}px, calc(${narrowPanelVw}vw - 14px))` : compact ? 'min(214px, 31vw)' : 'min(214px, 19vw)',
            transform: `scale(${effectiveScale})`,
            transformOrigin: 'top right',
          }}
        >
          <CompactHudCard accent={latestCombatEffect.meta.tone} padding={compact ? '8px 10px' : '9px 11px'}>
            <div style={{ display: 'grid', gap: 5 }}>
              <span style={{ fontSize: 9.5, letterSpacing: 1.6, textTransform: 'uppercase', color: latestCombatEffect.meta.tone }}>
                {latestCombatEffect.meta.label.toUpperCase()}
              </span>
              <span style={{ fontSize: compact ? 10 : 11, lineHeight: 1.25 }}>{latestCombatEffect.message}</span>
            </div>
          </CompactHudCard>
        </div>
      ) : null}

      {DEV_FEEL_TUNING_ENABLED && showFeelTuning ? (
        <DevFeelTuningPanel
          telemetry={movementTelemetry}
          localPlayer={localPlayer}
          remotePlayers={remotePlayers}
          combatCue={latestCombatEffect ?? latestCombatEventCue}
          compact={compact}
          hudScale={effectiveScale}
        />
      ) : null}

      {showDebugReplayOverlay ? (
        <DebugReplayOverlay
          summary={debugReplaySummary}
          latest={debugReplayLatest}
          compact={compact}
          hudScale={effectiveScale}
        />
      ) : null}

      {teamsUnbalanced ? (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: compact ? 42 : 48,
            transform: `translateX(-50%) scale(${effectiveScale})`,
            transformOrigin: 'top center',
            zIndex: 25,
          }}
        >
          <div
            style={{
              padding: '5px 14px',
              background: 'rgba(255, 180, 40, 0.12)',
              border: '1px solid rgba(255, 180, 40, 0.4)',
              clipPath: frameClip(),
              WebkitClipPath: frameClip(),
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#ffcc66', fontWeight: 700 }}>
              TEAMS UNBALANCED
            </span>
          </div>
        </div>
      ) : null}

      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: compact ? 10 : 14,
          width: topStripWidth,
          transform: `translateX(-50%) scale(${effectiveScale})`,
          transformOrigin: 'top center',
        }}
      >
        <CompactHudCard accent={objectiveAccent} padding={compact ? '8px 10px' : '8px 10px'}>
          <div style={{ display: 'grid', gap: compact ? 6 : 8 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: compact ? '72px minmax(0, 1fr) 72px' : '76px minmax(0, 1fr) 76px',
                gap: compact ? 8 : 8,
                alignItems: 'stretch',
              }}
            >
              <ScoreCell team="red" score={redScore} compact={compact} />
              <div style={{ display: 'grid', gap: 5, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontSize: compact ? 9.5 : 9.5,
                      letterSpacing: 1.7,
                      textTransform: 'uppercase',
                      color: 'rgba(231,244,252,0.7)',
                    }}
                  >
                    {objectiveMeta}
                  </span>
                  <span style={{
                    fontSize: compact ? 28 : 34,
                    lineHeight: 0.95,
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 700,
                    color:
                      redScore > blueScore
                        ? palette.red
                        : blueScore > redScore
                          ? palette.blue
                          : '#f4fbff',
                    textShadow: '0 0 14px rgba(0,0,0,0.55)',
                    letterSpacing: 1.2,
                  }}>
                    {formatClock(match.remainingSeconds)}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: compact ? 10 : 10.5,
                    lineHeight: 1.2,
                    letterSpacing: 0.4,
                    color: '#edf7ff',
                    whiteSpace: narrowCompact ? 'normal' : 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textTransform: 'uppercase',
                    maxWidth: '100%',
                  }}
                >
                  {missionSignal.title} · {objectiveText}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  <span
                    style={{
                      padding: '3px 7px',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      clipPath: frameClip(),
                      WebkitClipPath: frameClip(),
                      fontSize: 9,
                      letterSpacing: 1.2,
                      textTransform: 'uppercase',
                    }}
                  >
                    Red {compactFlagStatus(redFlag)}
                  </span>
                  <span
                    style={{
                      padding: '3px 7px',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      clipPath: frameClip(),
                      WebkitClipPath: frameClip(),
                      fontSize: 9,
                      letterSpacing: 1.2,
                      textTransform: 'uppercase',
                    }}
                  >
                    Blue {compactFlagStatus(blueFlag)}
                  </span>
                  {routePressure ? (
                    <span
                      style={{
                        padding: '3px 7px',
                        background: `${routePressure.tone}10`,
                        border: `1px solid ${routePressure.tone}28`,
                        clipPath: frameClip(),
                        WebkitClipPath: frameClip(),
                        fontSize: 9,
                        letterSpacing: 1.2,
                        textTransform: 'uppercase',
                        color: routePressure.tone,
                      }}
                    >
                      {routePressure.pressureText}
                    </span>
                  ) : null}
                </div>
              </div>
              <ScoreCell team="blue" score={blueScore} compact={compact} />
            </div>
          </div>
        </CompactHudCard>
      </div>

      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 2,
          left: '50%',
          transform: `translateX(-50%) scale(${effectiveScale})`,
          transformOrigin: 'top center',
          zIndex: 5,
          padding: '3px 10px',
          background: 'rgba(4,8,14,0.72)',
          border: '1px solid rgba(155,185,212,0.14)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          boxShadow: '0 0 12px rgba(0,0,0,0.18)',
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: 'rgba(200,240,255,0.7)',
            fontFamily: 'monospace',
            textAlign: 'center',
          }}
        >
          {compassLabel} · {Math.round(yawDeg).toString().padStart(3, '0')}°
        </div>
      </div>

      {gamePhase === 'playing' && (connectionState === 'reconnecting' || connectionState === 'connecting' || connectionState === 'offline') && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            top: 36,
            left: '50%',
            transform: `translateX(-50%) scale(${effectiveScale})`,
            transformOrigin: 'top center',
            zIndex: 30,
            animation: 'connBannerPulse 1.5s ease-in-out infinite',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 16px',
              background: connectionState === 'offline'
                ? 'linear-gradient(180deg, rgba(60,10,5,0.92), rgba(40,6,3,0.88))'
                : 'linear-gradient(180deg, rgba(50,35,5,0.92), rgba(35,25,3,0.88))',
              border: `1px solid ${connectionState === 'offline' ? 'rgba(255,100,60,0.5)' : 'rgba(255,200,80,0.4)'}`,
              clipPath: frameClip(),
              WebkitClipPath: frameClip(),
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              boxShadow: connectionState === 'offline'
                ? '0 0 16px rgba(255,80,40,0.2), inset 0 0 12px rgba(255,80,40,0.06)'
                : '0 0 16px rgba(255,200,80,0.15), inset 0 0 12px rgba(255,200,80,0.04)',
            }}
          >
            <div style={{ display: 'flex', gap: 4 }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: connectionState === 'offline' ? '#ff6444' : '#ffcc66',
                    animation: `connDotBlink 1.2s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </div>
            <span
              style={{
                fontSize: 11,
                letterSpacing: 2.5,
                textTransform: 'uppercase',
                fontFamily: 'monospace',
                fontWeight: 700,
                color: connectionState === 'offline' ? '#ff8d74' : '#ffcc66',
                textShadow: connectionState === 'offline'
                  ? '0 0 10px rgba(255,100,60,0.5)'
                  : '0 0 10px rgba(255,200,80,0.4)',
              }}
            >
              {connectionState === 'offline' ? 'CONNECTION LOST' : 'RECONNECTING'}
            </span>
          </div>
        </div>
      )}

      {/* Kill Confirmation Banner */}
      {killBanner ? (
        <div
          style={{
            position: 'absolute',
            top: 26,
            left: '50%',
            transform: `translateX(-50%) scale(${effectiveScale})`,
            transformOrigin: 'top center',
            zIndex: 20,
            animation: 'killBannerFade 2.5s ease-in-out forwards',
          }}
        >
          <div
            style={{
              padding: '10px 18px',
              background: 'linear-gradient(180deg, rgba(5,10,17,0.92), rgba(4,8,14,0.85))',
              borderLeft: `4px solid ${classifyWeaponRole(killBanner.weaponId).tone}`,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              borderRight: '1px solid rgba(255,255,255,0.08)',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              clipPath: frameClip(),
              WebkitClipPath: frameClip(),
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              boxShadow: `0 0 20px rgba(0,0,0,0.22)`,
            }}
          >
            <span
              style={{
                fontSize: 13,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                color: '#f4fbff',
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              ELIMINATED {killBanner.name}
            </span>
          </div>
        </div>
      ) : null}

      {/* Team Flag Status Icons */}
      {!narrowCompact ? (
        <>
          <div
            style={{
              position: 'absolute',
              top: 2,
              left: compact ? 12 : 16,
              transform: `scale(${effectiveScale})`,
              transformOrigin: 'top left',
              zIndex: 5,
            }}
          >
            <CompactHudCard accent={teamTone(localPlayer.team)} padding="6px 8px">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 8, height: 14, background: teamTone(localPlayer.team) }} />
                <div style={{ display: 'grid', gap: 1 }}>
                  <span style={{ fontSize: 8, letterSpacing: 1.2, textTransform: 'uppercase', color: teamTone(localPlayer.team) }}>
                    {(localPlayer.team ?? '').toString().toUpperCase() || 'TEAM'}
                  </span>
                  <span style={{ fontSize: 9, letterSpacing: 0.8, textTransform: 'uppercase', color: '#edf7ff' }}>
                    {compactFlagStatus(flags.find((f) => (f.team ?? '').toLowerCase() === normalizeTeamKey(localPlayer.team)))}
                  </span>
                </div>
              </div>
            </CompactHudCard>
          </div>
          <div
            style={{
              position: 'absolute',
              top: 2,
              right: compact ? 12 : 16,
              transform: `scale(${effectiveScale})`,
              transformOrigin: 'top right',
              zIndex: 5,
            }}
          >
            <CompactHudCard accent={teamTone(localPlayer.team === 'red' ? 'blue' : 'red')} padding="6px 8px">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: 'row-reverse' }}>
                <div style={{ width: 8, height: 14, background: teamTone(localPlayer.team === 'red' ? 'blue' : 'red') }} />
                <div style={{ display: 'grid', gap: 1, textAlign: 'right' }}>
                  <span style={{ fontSize: 8, letterSpacing: 1.2, textTransform: 'uppercase', color: teamTone(localPlayer.team === 'red' ? 'blue' : 'red') }}>
                    {localPlayer.team === 'red' ? 'BLUE' : 'RED'}
                  </span>
                  <span style={{ fontSize: 9, letterSpacing: 0.8, textTransform: 'uppercase', color: '#edf7ff' }}>
                    {compactFlagStatus(flags.find((f) => (f.team ?? '').toLowerCase() === (localPlayer.team === 'red' ? 'blue' : 'red')))}
                  </span>
                </div>
              </div>
            </CompactHudCard>
          </div>
        </>
      ) : null}

      {!narrowCompact && !dynamicHudSuppressed ? (
        <div
          style={{
            position: 'absolute',
            right: compact ? 12 : 16,
            top: compact ? 12 : 16,
            width: compact ? 'min(214px, 31vw)' : 'min(214px, 19vw)',
            display: 'grid',
            gap: 8,
            transform: `scale(${effectiveScale})`,
            transformOrigin: 'top right',
          }}
        >
          <BaseSystemsPanel
            localPlayer={localPlayer}
            generators={generators}
            sensors={sensors}
            shieldDoors={shieldDoors}
            inventoryStations={inventoryStations}
            repairZones={repairZones}
            turrets={turrets}
            compact={compact}
            minimal={compactUltra || !compact}
          />
          {!compactUltra ? (
            <CompactHudCard accent={latestEventTone} padding={compact ? '8px 10px' : '9px 11px'}>
              <div style={{ display: 'grid', gap: 5 }}>
                <span style={{ fontSize: 9.5, letterSpacing: 1.6, textTransform: 'uppercase', color: latestEventTone }}>
                  {latestEventLabel}
                </span>
                <span style={{ fontSize: compact ? 10 : 11, lineHeight: 1.25 }}>{latestEventText}</span>
              </div>
            </CompactHudCard>
          ) : null}
        </div>
      ) : null}

      {currentTutorialStep && !showHelp && !showSettings ? (
        <div
          data-hud-tutorial-dock={tutorialAlign}
          style={{
            position: 'absolute',
            top: touchCompactHud ? 148 : compact ? 118 : 132,
            left: compact ? 12 : 392,
            zIndex: 9,
            transform: `scale(${effectiveScale})`,
            transformOrigin: 'top left',
          }}
        >
          <TutorialOverlay
            completedStepIds={tutorialProgress.completedStepIds}
            currentStep={currentTutorialStep}
            align={tutorialAlign}
            onDismiss={() => setTutorialProgress(dismissTutorial())}
          />
        </div>
      ) : null}

      {/* Kill Feed */}
      {!narrowCompact && !dynamicHudSuppressed && events.length > 0 ? (
        <div
          style={{
            position: 'absolute',
            right: compact ? 12 : 16,
            top: compact ? 240 : 230,
            width: compact ? 'min(214px, 31vw)' : 'min(214px, 19vw)',
            transform: `scale(${effectiveScale})`,
            transformOrigin: 'top right',
            display: 'grid',
            gap: 4,
            zIndex: 5,
          }}
        >
          {events.slice(-5).reverse().map((event, idx) => {
            const category = (event.category ?? 'system').toLowerCase();
            const tone =
              category === 'combat' || category === 'critical' ? palette.danger :
              category === 'objective' ? '#ffcc66' :
              category === 'support' ? palette.support :
              palette.accent;
            const isHighPriority = (event.priority ?? 0) > 5;
            return (
              <div
                key={event.id ?? `evt-${idx}`}
                style={{
                  padding: '6px 8px',
                  background: 'linear-gradient(180deg, rgba(5,10,17,0.88), rgba(4,8,14,0.78))',
                  borderLeft: `4px solid ${tone}`,
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  borderRight: '1px solid rgba(255,255,255,0.06)',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  clipPath: frameClip(),
                  WebkitClipPath: frameClip(),
                  fontSize: 10,
                  lineHeight: 1.2,
                  color: isHighPriority ? '#f4fbff' : 'rgba(237,247,255,0.82)',
                  fontWeight: isHighPriority ? 700 : 400,
                  animation: 'killFeedSlideIn 0.2s ease-out',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  boxShadow: `0 0 12px rgba(0,0,0,0.18)`,
                }}
              >
                {event.message}
              </div>
            );
          })}
        </div>
      ) : null}

      <div
        data-hud-bottom-stack={touchCompactHud ? 'mobile-touch' : undefined}
        style={{
          position: 'absolute',
          left: narrowCompact ? 12 : 16,
          bottom: bottomDockOffset,
          width: bottomLeftWidth,
          display: 'grid',
          gap: 8,
          transform: `scale(${effectiveScale})`,
          transformOrigin: 'bottom left',
          animation: 'hudSlideIn 0.4s ease forwards',
        }}
      >
        <MovementTelemetryStrip telemetry={movementTelemetry} compact={compact || narrowCompact} minimal={hudMinimal} />
        {/* Phase 67 — prominent T2 energy/jet bar centered in bottom dock */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '6px 10px',
          background: 'linear-gradient(180deg, rgba(8,18,32,0.78), rgba(4,10,20,0.78))',
          border: '1px solid rgba(127,223,255,0.22)',
          clipPath: 'polygon(0 6px, 6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px))',
          WebkitClipPath: 'polygon(0 6px, 6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px))',
        }}>
          <span style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#7fdfff', fontWeight: 800, textShadow: '0 0 6px #7fdfff44' }}>JET</span>
          <SegmentedEnergyBar energy={movementTelemetry?.energy ?? localPlayer.energy} />
        </div>
        {/* Phase 130 — Pack-energy meter sits below the JET bar; glows when pack is active. */}
        <PackEnergyMeter
          packId={(localPlayer.packId ?? null) as string | null}
          packEnergy={Number((localPlayer as { packEnergy?: number }).packEnergy ?? 100)}
          packActiveSeconds={Number((localPlayer as { packActiveSeconds?: number }).packActiveSeconds ?? 0)}
          packCooldown={Number((localPlayer as { packCooldown?: number }).packCooldown ?? 0)}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '0 2px' }}>
            <span style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: palette.red, fontWeight: 700 }}>RED {displayRedCount}</span>
            <span style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(231,244,252,0.5)' }}>vs</span>
            <span style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: palette.blue, fontWeight: 700 }}>BLUE {displayBlueCount}</span>
        </div>
        {!touchCompactHud && occupiedVehicle ? (
          <CompactHudCard accent="#ffcc66" padding={compact ? '9px 10px' : '10px 12px'}>
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 9.5, letterSpacing: 1.6, textTransform: 'uppercase', color: '#ffcc66' }}>
                  Vehicle
                </span>
                <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#f4fbff' }}>
                  {String(occupiedVehicle.vehicleId ?? 'vehicle').replace(/-/g, ' ')}
                </span>
              </div>
              <MeterRow
                label="Hull"
                value={`${Math.round(Number(occupiedVehicle.health ?? 0))}`}
                percent={(Number(occupiedVehicle.health ?? 0) / Number(occupiedVehicle.maxHealth || 1)) * 100}
                tone={resolveHealthBarColor(Number(occupiedVehicle.health ?? 0), Number(occupiedVehicle.maxHealth || 1))}
              />
              {occupiedVehicle.weaponId ? (() => {
                const vehicleWeaponDef = WEAPONS[occupiedVehicle.weaponId as keyof typeof WEAPONS];
                const vehicleWeaponIdentity = getWeaponIdentity(occupiedVehicle.weaponId);
                const vehicleCooldown = Number(occupiedVehicle.weaponCooldown ?? 0);
                const vehicleCooldownMax = vehicleWeaponDef?.cooldownSeconds ?? 1;
                const vehicleAmmo = occupiedVehicle.ammo;
                const infiniteAmmo = vehicleAmmo == null || vehicleAmmo < 0;
                return (
                  <div style={{ display: 'grid', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 9, letterSpacing: 1.4, textTransform: 'uppercase', color: '#ff8d74' }}>
                        Weapon
                      </span>
                      <span style={{ fontSize: 10, textTransform: 'uppercase', color: '#f4fbff' }}>
                        {vehicleWeaponIdentity?.label ?? String(occupiedVehicle.weaponId).replace(/-/g, ' ')}
                      </span>
                    </div>
                    <MeterRow
                      label="Cooldown"
                      value={vehicleCooldown > 0 ? `${vehicleCooldown.toFixed(1)}s` : 'READY'}
                      percent={(1 - vehicleCooldown / vehicleCooldownMax) * 100}
                      tone={vehicleCooldown > 0 ? '#ffcc66' : '#8ef0c9'}
                    />
                    {!infiniteAmmo ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 9, letterSpacing: 1.1, textTransform: 'uppercase' }}>
                        <span style={{ color: 'rgba(231,244,252,0.62)' }}>Ammo</span>
                        <span style={{ color: (vehicleAmmo ?? 0) > 5 ? '#f4fbff' : '#ff8d74', fontVariantNumeric: 'tabular-nums' }}>
                          {vehicleAmmo}
                        </span>
                      </div>
                    ) : null}
                  </div>
                );
              })() : null}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, letterSpacing: 1.1, textTransform: 'uppercase', color: 'rgba(231,244,252,0.62)' }}>
                <span>Speed {Math.round(Math.hypot(Number((occupiedVehicle.velocity as Record<string, number> | undefined)?.x ?? 0), Number((occupiedVehicle.velocity as Record<string, number> | undefined)?.z ?? 0)))} m/s</span>
                <span style={{ color: '#ffcc66' }}>Press V to exit</span>
              </div>
              {/* Phase 178 — flight readout for air vehicles (Shrike): altitude + afterburner. */}
              {occupiedVehicle.vehicleId === 'shrike' ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, letterSpacing: 1.1, textTransform: 'uppercase', color: 'rgba(231,244,252,0.62)' }}>
                  <span>ALT {Math.round(Number((occupiedVehicle.position as Record<string, number> | undefined)?.y ?? 0))} m</span>
                  <span style={{ color: occupiedVehicle.boostActive ? '#7dff9c' : (occupiedVehicle.boostCooldownSeconds ?? 0) > 0 ? '#ff8d74' : '#62ddff' }}>
                    {occupiedVehicle.boostActive ? 'AFTERBURNER' : (occupiedVehicle.boostCooldownSeconds ?? 0) > 0 ? 'BURN COOLDOWN' : 'BURN READY'}
                  </span>
                </div>
              ) : null}
            </div>
          </CompactHudCard>
        ) : !touchCompactHud && nearestVehiclePadCue ? (
          <CompactHudCard accent={nearestVehiclePadCue.tone} padding={compact ? '8px 10px' : '9px 11px'}>
            <div style={{ display: 'grid', gap: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 9.5, letterSpacing: 1.6, textTransform: 'uppercase', color: nearestVehiclePadCue.tone }}>
                  Vehicle Pad
                </span>
                <span style={{ fontSize: 10.5, textTransform: 'uppercase', color: '#f4fbff' }}>
                  {nearestVehiclePadCue.vehicleName}
                </span>
              </div>
              <span style={{ fontSize: compact || narrowCompact ? 10 : 11, lineHeight: 1.15, letterSpacing: 0.7, textTransform: 'uppercase', color: '#edf7ff' }}>
                {nearestVehiclePadCue.status}
              </span>
              <span style={{ fontSize: 9, letterSpacing: 1.1, textTransform: 'uppercase', color: nearestVehiclePadCue.tone }}>
                {nearestVehiclePadCue.footer}
              </span>
            </div>
          </CompactHudCard>
        ) : null}
        {!touchCompactHud ? (
          <div
            key={weaponSwapKey}
            style={{ animation: 'weaponSwapIn 0.2s ease-out' }}
          >
            <WeaponPanel
              localPlayer={localPlayer}
              compact={compact || narrowCompact}
              minimal={hudMinimal}
              stacked
            />
          </div>
        ) : null}
      </div>

      <div
        role="img"
        aria-label="Radar showing nearby players, objectives, and vehicles"
        data-hud-radar-dock={touchCompactHud ? 'mobile-touch' : undefined}
        data-hud-radar-variant={touchCompactHud ? 'mobile-micro' : undefined}
        style={{
          position: 'absolute',
          right: narrowCompact ? 12 : 16,
          bottom: bottomDockOffset,
          width: radarWidth,
          transform: `scale(${effectiveScale})`,
          transformOrigin: 'bottom right',
        }}
      >
        <Radar
          localPlayer={localPlayer}
          remotePlayers={remotePlayers}
          flags={flags}
          boostPads={boostPads}
          inventoryStations={inventoryStations}
          vehicles={vehicles}
          deployables={deployables}
          tacticalPings={tacticalPings}
          routeCaptureZones={routeCaptureZones}
          objectiveScoringHints={objectiveScoringHints}
          ctfMacro={ctfMacro}
          sensorBoost={Boolean((localPlayer as Record<string, unknown>).sensorBoost)}
          compact
          narrow={narrowCompact}
          minimal={touchCompactHud}
          reducedMotion={cameraSettings.reducedMotion}
          colorMode={cameraSettings.colorMode}
        />
      </div>

      {cameraSettings.captionsEnabled && prioritizedEvents.length > 0 ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: narrowCompact ? 104 : 72,
            transform: `translateX(-50%) scale(${effectiveScale})`,
            transformOrigin: 'bottom center',
            width: 'min(520px, calc(100vw - 28px))',
            display: 'grid',
            gap: 4,
            zIndex: 24,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 9, letterSpacing: 1.6, textTransform: 'uppercase', color: palette.accent }}>CAPTIONS</div>
          {prioritizedEvents.slice(0, 2).map((event) => (
            <div
              key={event.event.id ?? event.index}
              style={{
                padding: '6px 10px',
                background: 'rgba(4,8,14,0.82)',
                border: `1px solid ${event.meta.tone}66`,
                color: '#f4fbff',
                fontSize: 12,
                lineHeight: 1.25,
                clipPath: frameClip(),
                WebkitClipPath: frameClip(),
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                boxShadow: `0 0 12px rgba(0,0,0,0.18)`,
              }}
            >
              {event.message}
            </div>
          ))}
        </div>
      ) : null}

      <TouchControlsOverlay visible={cameraSettings.touchControlsEnabled || coarsePointer} />

      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            `radial-gradient(circle at center, ${palette.accent}0d 0%, transparent 22%), radial-gradient(circle at center, transparent 60%, rgba(0,0,0,0.14) 100%)`,
          mixBlendMode: 'screen',
          opacity: cameraSettings.reducedMotion ? 0.22 : 0.5,
        }}
      />
      {/* Damage direction indicators / vignette */}
      {damageFlash ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'radial-gradient(circle at center, transparent 50%, rgba(255,40,20,0.35) 100%)',
            mixBlendMode: 'screen',
            animation: 'damageVignette 0.4s ease-out forwards',
            transition: 'opacity 0.3s ease',
            zIndex: 10,
          }}
        />
      ) : null}
      {/* Self-damage vignette */}
      {selfDamageFlash ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'radial-gradient(circle at center, transparent 40%, rgba(255,20,10,0.45) 100%)',
            mixBlendMode: 'screen',
            animation: 'selfDamageVignette 0.3s ease-out forwards',
            zIndex: 11,
          }}
        />
      ) : null}
      {/* Headshot flash */}
      {headshotFlash ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: '38%',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 12,
            fontSize: 28,
            fontWeight: 900,
            letterSpacing: 6,
            textTransform: 'uppercase',
            color: '#ffd700',
            textShadow: '0 0 18px rgba(255,215,0,0.8), 0 0 40px rgba(255,215,0,0.4)',
            animation: 'damageVignette 0.5s ease-out forwards',
          }}
        >
          HEADSHOT
        </div>
      ) : null}
      {(localPlayer.health ?? 100) < 30 && (localPlayer.health ?? 100) > 0 ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'radial-gradient(circle at center, transparent 40%, rgba(255,20,10,0.3) 100%)',
            animation: 'lowHealthPulse 1s ease-in-out infinite',
            zIndex: 9,
          }}
        />
      ) : null}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: (occupiedVehicle ? reticleSize + 24 : reticleSize) + crosshairFlick * 2,
          height: (occupiedVehicle ? reticleSize + 24 : reticleSize) + crosshairFlick * 2,
          marginLeft: -((occupiedVehicle ? reticleSize + 24 : reticleSize) / 2 + crosshairFlick),
          marginTop: -((occupiedVehicle ? reticleSize + 24 : reticleSize) / 2 + crosshairFlick),
          transition: crosshairFlick > 0 ? 'none' : 'width 0.08s ease-out, height 0.08s ease-out, margin 0.08s ease-out',
          animation: cameraSettings.reducedMotion ? undefined : 'reticleBreathe 2s ease-in-out infinite',
        }}
      >
        {occupiedVehicle && occupiedVehicle.weaponId ? (() => {
          const vehicleWeaponDef = WEAPONS[occupiedVehicle.weaponId as keyof typeof WEAPONS];
          const vehicleCooldown = Number(occupiedVehicle.weaponCooldown ?? 0);
          const vehicleCooldownMax = vehicleWeaponDef?.cooldownSeconds ?? 1;
          const cooldownRatio = vehicleCooldown > 0 ? vehicleCooldown / vehicleCooldownMax : 0;
          const vehicleAmmo = occupiedVehicle.ammo;
          const infiniteAmmo = vehicleAmmo == null || vehicleAmmo < 0;
          const vReticleSize = reticleSize + 24;
          return (
            <>
              <svg
                width={vReticleSize}
                height={vReticleSize}
                viewBox={`0 0 ${vReticleSize} ${vReticleSize}`}
                style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}
              >
                <circle
                  cx={vReticleSize / 2}
                  cy={vReticleSize / 2}
                  r={vReticleSize / 2 - 6}
                  fill="none"
                  stroke={cooldownRatio > 0 ? 'rgba(255,204,102,0.45)' : 'rgba(142,240,201,0.7)'}
                  strokeWidth={cooldownRatio > 0 ? 2 : 2.5}
                  strokeDasharray={cooldownRatio > 0 ? `${(1 - cooldownRatio) * 2 * Math.PI * (vReticleSize / 2 - 6)} ${2 * Math.PI * (vReticleSize / 2 - 6)}` : 'none'}
                  strokeLinecap="round"
                  transform={`rotate(-90 ${vReticleSize / 2} ${vReticleSize / 2})`}
                />
                <line x1={vReticleSize / 2} y1={4} x2={vReticleSize / 2} y2={14} stroke="rgba(200,240,255,0.5)" strokeWidth={1.5} />
                <line x1={vReticleSize / 2} y1={vReticleSize - 14} x2={vReticleSize / 2} y2={vReticleSize - 4} stroke="rgba(200,240,255,0.5)" strokeWidth={1.5} />
                <line x1={4} y1={vReticleSize / 2} x2={14} y2={vReticleSize / 2} stroke="rgba(200,240,255,0.5)" strokeWidth={1.5} />
                <line x1={vReticleSize - 14} y1={vReticleSize / 2} x2={vReticleSize - 4} y2={vReticleSize / 2} stroke="rgba(200,240,255,0.5)" strokeWidth={1.5} />
              </svg>
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: 5,
                  height: 5,
                  marginLeft: -2.5,
                  marginTop: -2.5,
                  background: cooldownRatio > 0 ? '#ffcc66' : '#8ef0c9',
                  borderRadius: '50%',
                  boxShadow: `0 0 10px ${cooldownRatio > 0 ? 'rgba(255,204,102,0.6)' : 'rgba(142,240,201,0.7)'}`,
                }}
              />
              {!infiniteAmmo ? (
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: vReticleSize / 2 + 12,
                    transform: 'translateX(-50%)',
                    fontSize: 11,
                    letterSpacing: 1.5,
                    fontWeight: 700,
                    color: (vehicleAmmo ?? 0) > 5 ? '#f4fbff' : '#ff8d74',
                    textShadow: '0 0 6px rgba(0,0,0,0.7)',
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {vehicleAmmo}
                </div>
              ) : null}
            </>
          );
        })() : (
        <>
        {/* Speedometer arc gauge */}
        <svg
          width={reticleSize + 28}
          height={reticleSize + 28}
          viewBox={`0 0 ${reticleSize + 28} ${reticleSize + 28}`}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            overflow: 'visible',
            pointerEvents: 'none',
          }}
        >
          <circle
            cx={(reticleSize + 28) / 2}
            cy={(reticleSize + 28) / 2}
            r={(reticleSize + 28) / 2 - 4}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth={2.5}
          />
          <circle
            cx={(reticleSize + 28) / 2}
            cy={(reticleSize + 28) / 2}
            r={(reticleSize + 28) / 2 - 4}
            fill="none"
            stroke={(() => {
              const s = localPlayer.speed ?? 0;
              if (s >= 75) return '#ff8d74';
              if (s >= 40) return '#ffcc66';
              return '#62ddff';
            })()}
            strokeWidth={3}
            strokeDasharray={`${(Math.min(100, localPlayer.speed ?? 0) / 100) * 2 * Math.PI * ((reticleSize + 28) / 2 - 4)} ${2 * Math.PI * ((reticleSize + 28) / 2 - 4)}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${(reticleSize + 28) / 2} ${(reticleSize + 28) / 2})`}
            style={{ opacity: 0.8 }}
          />
        </svg>

        {/* Outer ring */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            border: hitMarker.active ? `3px solid ${hitMarker.color}` : '2px solid rgba(200,240,255,0.65)',
            borderRadius: '50%',
            transition: hitMarker.active ? 'none' : 'border-width 0.15s ease-out',
            animation: hitMarker.active && hitMarker.color === '#ff4444'
              ? 'reticleHitFlash 0.15s ease-out'
              : killBanner
                ? 'reticleKillPulse 0.3s ease-out'
                : undefined,
          }}
        />
        {/* Hit marker expanding ring */}
        {hitMarker.active ? (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              border: `2px solid ${hitMarker.color}`,
              borderRadius: '50%',
              animation: 'hitMarkerExpand 0.15s ease-out forwards',
            }}
          />
        ) : null}
        {/* Inner dot */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 4,
            height: 4,
            marginLeft: -2,
            marginTop: -2,
            background: '#f4fbff',
            borderRadius: '50%',
            boxShadow: '0 0 8px rgba(255,255,255,0.6)',
          }}
        />
        {/* Bottom speed tick arc */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: -8,
            transform: 'translateX(-50%)',
            width: reticleSize * 0.75,
            height: reticleSize * 0.4,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: '100%',
              height: '200%',
              borderRadius: '50%',
              borderBottom: `${2 + speedMix * 3}px solid ${(() => {
                const s = localPlayer.speed ?? 0;
                if (s >= 75) return '#ff8d74';
                if (s >= 40) return '#ffcc66';
                return '#62ddff';
              })()}`,
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              opacity: 0.7 + speedMix * 0.3,
            }}
          />
        </div>
        </>
        )}
      </div>

      {scorePopups.length > 0 ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: '42%',
            transform: `translateX(-50%) scale(${effectiveScale})`,
            transformOrigin: 'center center',
            zIndex: 22,
            pointerEvents: 'none',
          }}
        >
          {scorePopups.map((popup) => (
            <div
              key={popup.id}
              style={{
                textAlign: 'center',
                fontSize: 22,
                fontWeight: 900,
                letterSpacing: 3,
                textTransform: 'uppercase',
                color: '#ffcc66',
                textShadow: '0 0 14px rgba(255,204,102,0.7), 0 0 30px rgba(255,204,102,0.3)',
                animation: 'scoreFloatUp 1.2s ease-out forwards',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {popup.text}
            </div>
          ))}
        </div>
      ) : null}

      {oobWarning ? (
        <>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              boxShadow: 'inset 0 0 120px 40px rgba(255,20,20,0.18)',
              animation: 'oobPulse 0.5s ease-in-out infinite',
              zIndex: 12,
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: compact ? 64 : 72,
              left: '50%',
              transform: `translateX(-50%) scale(${effectiveScale})`,
              transformOrigin: 'top center',
              zIndex: 22,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                padding: '12px 28px',
                background: 'rgba(180,10,10,0.35)',
                border: '2px solid rgba(255,40,40,0.7)',
                clipPath: frameClip(),
                WebkitClipPath: frameClip(),
                animation: 'oobTextFlash 0.5s ease-in-out infinite',
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase', color: '#ff4040' }}>
                OUT OF BOUNDS
              </div>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,200,200,0.85)', marginTop: 4 }}>
                RETURN TO BATTLEFIELD
              </div>
            </div>
          </div>
        </>
      ) : null}

      {inWater ? (
        <>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: 'rgba(20,60,120,0.25)',
              animation: 'waterWave 2s ease-in-out infinite',
              zIndex: 13,
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: compact ? 64 : 72,
              left: '50%',
              transform: `translateX(-50%) scale(${effectiveScale})`,
              transformOrigin: 'top center',
              zIndex: 23,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                padding: '8px 20px',
                background: 'rgba(20,60,120,0.4)',
                border: '1px solid rgba(100,180,255,0.5)',
                clipPath: frameClip(),
                WebkitClipPath: frameClip(),
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#6ab8ff' }}>
                DROWNING
              </div>
            </div>
          </div>
        </>
      ) : null}

      {showScoreboard && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 90, pointerEvents: 'auto' }}>
          <Scoreboard
            visible={showScoreboard}
            players={allPlayers}
            teamScores={match.teamScores ?? {}}
            localPlayerId={localPlayer.id}
            events={events as never}
          />
        </div>
      )}

      {showSettings && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, pointerEvents: 'auto' }}>
          <SettingsPanel
            visible={showSettings}
            settings={cameraSettings}
            onSettingsChange={handleSettingsChange}
            onClose={handleSettingsClose}
          />
        </div>
      )}

      {showHelp && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 80, pointerEvents: 'auto' }}>
          <HelpOverlay connected={connected} compact={compact} minimal={compactUltra} />
        </div>
      )}

      <FloatingDamages />
      <KillStreakBanner />
      <HitIndicator />
      {/* Phase 171 — laser-rifle sniper scope overlay */}
      <SniperScope />
      <ChatOverlay sendMessage={networkSendMessage} />

      {/* Phase 112 — Enemy IFF bracket on aim target */}
      <EnemyIFFBracket localPlayer={localPlayer as never} remotePlayers={remotePlayers as never} />
      {/* Phase 129 — slow-projectile target leading reticle. */}
      <LeadReticle localPlayer={localPlayer as never} remotePlayers={remotePlayers as never} />

      {/* Phase 117 — Distance-fading friendly nameplates */}
      <Nameplates localPlayer={localPlayer as never} remotePlayers={remotePlayers as never} />

      {/* Phase 113 — Voice commands menu (V) */}
      <VoiceCommandMenu sendMessage={networkSendMessage} />

      {/* Phase 118 — Contextual objective hint banner */}
      <ObjectiveHintBanner
        localPlayerId={localPlayer.id}
        localTeam={localPlayer.team}
        carryingFlagTeam={localPlayer.carryingFlagTeam}
        flags={flags}
      />

      {/* Phase 156 — edge-pointing waypoint markers (off-screen) or inline icons. */}
      <WaypointMarkers waypoints={pingWaypoints as never} localPlayer={localPlayer as never} />

      {/* Phase 157 — team status panel (small left-side list). */}
      <TeamStatusPanel
        localPlayer={localPlayer as never}
        remotePlayers={remotePlayers as never}
        flags={flags}
        vehicles={vehicles as never}
      />

      {/* Phase 159 — spectator overlay when local player is dead. */}
      <SpectatorOverlay localPlayer={localPlayer as never} remotePlayers={remotePlayers as never} />

      {/* Phase 111 — Command map overlay (M). Phase 152/155 add sensor + waypoint props. */}
      <CommandMap
        visible={showCommandMap}
        localPlayer={localPlayer as never}
        remotePlayers={remotePlayers as never}
        flags={flags}
        vehicles={vehicles as never}
        sensors={sensors as never}
        pingWaypoints={pingWaypoints as never}
        onPing={(worldX, worldZ) => {
          networkSendMessage({ type: 'ping-waypoint', x: worldX, z: worldZ, y: 0 });
        }}
      />

      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: 2,
          background: 'linear-gradient(90deg, transparent, rgba(98,221,255,0.08), transparent)',
          animation: 'hudScanline 8s linear infinite',
          pointerEvents: 'none',
          zIndex: 50,
        }}
      />

      {(() => {
        const localTeam = (localPlayer.team ?? '').toLowerCase();
        const ownFlag = flags.find((flag) => (flag.team ?? '').toLowerCase() === localTeam);
        const ownFlagStolen = Boolean(ownFlag?.holderId);
        return ownFlagStolen ? (
          <div style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            border: '3px solid #ff2200',
            boxSizing: 'border-box',
            animation: 'flagStolen 0.67s ease-in-out infinite',
            zIndex: 999,
          }} />
        ) : null;
      })()}

    </div>
  );
}

// ─── Polish helpers ───────────────────────────────────────────────────────────

export function resolveHealthBarStyle(health: number, maxHealth: number): { pulsing: boolean; color: string } {
  const safeMaxHealth = Math.max(1, maxHealth);
  const ratio = Math.max(0, Math.min(1, health / safeMaxHealth));
  const pulsing = ratio < 0.25;

  // Preserve legacy resolveHealthBarStyle palette for compatibility with established callers/tests.
  if (ratio >= 0.6) return { pulsing, color: '#44ff88' };
  if (ratio >= 0.25) return { pulsing, color: '#ff8844' };
  return { pulsing, color: '#ff2222' };

}

export function resolveCaptureFlashStyle(ageSeconds: number): { scale: number; opacity: number } {
  const duration = 1.5;
  const progress = Math.min(1, ageSeconds / duration);
  const scale = 2.0 - progress;
  const opacity = progress > 0.7 ? 1 - (progress - 0.7) / 0.3 : 1;
  return { scale, opacity };
}


// ─── Phase 156 — Waypoint edge markers ────────────────────────────────────────
// Projects active team waypoints to the screen edge with an arrow pointing
// toward each one, relative to the local player's yaw.

type WaypointMarkerEntry = {
  id: string;
  position: { x: number; y: number; z: number };
  color?: string | null;
  callerName?: string | null;
  kind?: string | null;
  ttlSeconds?: number;
};

function WaypointMarkers({
  waypoints,
  localPlayer,
}: {
  waypoints: WaypointMarkerEntry[];
  localPlayer: { position?: { x?: number; y?: number; z?: number }; yaw?: number };
}) {
  if (!waypoints || waypoints.length === 0) return null;
  const px = localPlayer.position?.x ?? 0;
  const pz = localPlayer.position?.z ?? 0;
  const yaw = typeof localPlayer.yaw === 'number' ? localPlayer.yaw : 0;
  return (
    <div
      aria-hidden="true"
      style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 65 }}
    >
      {waypoints.map((wp) => {
        const dx = wp.position.x - px;
        const dz = wp.position.z - pz;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < 1) return null;
        // Convert world delta to player-relative bearing. yaw=0 looks toward -Z
        // in the existing camera convention; we just want a consistent compass
        // ring so use atan2 then subtract yaw.
        const worldAngle = Math.atan2(dx, -dz);
        const relative = worldAngle - yaw;
        const onScreenForward = Math.cos(relative) > 0.35;
        // Place on the rim of a circle inset 60px from window edges.
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        const r = Math.min(window.innerWidth, window.innerHeight) / 2 - 64;
        const sx = cx + Math.sin(relative) * r;
        const sy = cy - Math.cos(relative) * r * (onScreenForward ? 0.6 : 1);
        const color = wp.color ?? '#62ddff';
        const label = wp.kind === 'repair-needed' ? 'REPAIR' : wp.callerName ?? 'PING';
        const distLabel = `${Math.round(dist)}m`;
        return (
          <div
            key={`wp-marker-${wp.id}`}
            style={{
              position: 'absolute',
              left: sx,
              top: sy,
              transform: 'translate(-50%, -50%)',
              padding: '3px 7px',
              border: `1px solid ${color}`,
              borderRadius: 3,
              background: 'rgba(0,8,16,0.55)',
              color,
              fontFamily: 'monospace',
              fontSize: 10,
              letterSpacing: 1.2,
              whiteSpace: 'nowrap',
              boxShadow: `0 0 12px ${color}55`,
            }}
          >
            <span style={{ marginRight: 6 }}>{label}</span>
            <span style={{ opacity: 0.75 }}>{distLabel}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Phase 157 — Team status panel ────────────────────────────────────────────
// Compact left-side list showing teammates with role, HP bar, and a status
// icon (alive/dead/in-vehicle/at-flag). Refreshes from snapshot props.

type TeamPanelPlayer = {
  id?: string;
  displayName?: string;
  team?: string | null;
  health?: number;
  respawnTimer?: number;
  carryingFlagTeam?: string | null;
  botRole?: string;
  armorClass?: string;
  position?: { x?: number; y?: number; z?: number };
};

type TeamPanelFlag = { team?: string | null; holderId?: string | null; state?: string | null };
type TeamPanelVehicle = { occupiedBy?: string | null; active?: boolean };

function statusForTeammate(
  p: TeamPanelPlayer,
  vehicles: TeamPanelVehicle[],
  flags: TeamPanelFlag[],
): { icon: string; tone: string; label: string } {
  if ((p.respawnTimer ?? 0) > 0 || (p.health ?? 0) <= 0) {
    return { icon: '✕', tone: '#ff5555', label: 'DOWN' };
  }
  if (p.id && vehicles.some((v) => v.occupiedBy === p.id && v.active !== false)) {
    return { icon: 'V', tone: '#ffcc66', label: 'VHCL' };
  }
  if (p.carryingFlagTeam) {
    return { icon: '⚑', tone: '#ffd86b', label: 'FLAG' };
  }
  const team = (p.team ?? '').toLowerCase();
  const ownFlag = flags.find((f) => (f.team ?? '').toLowerCase() === team);
  if (ownFlag && !ownFlag.holderId && p.position) {
    // Approximate at-flag if very close to own flag home, optional. Skip — keep simple.
  }
  return { icon: '●', tone: '#88ffaa', label: 'OK' };
}

function roleLabelForPlayer(p: TeamPanelPlayer): string {
  if (p.botRole) return p.botRole.toUpperCase();
  const armor = (p.armorClass ?? '').toLowerCase();
  if (armor === 'light') return 'LIGHT';
  if (armor === 'heavy') return 'HEAVY';
  if (armor === 'medium') return 'MED';
  return 'PLR';
}

function TeamStatusPanel({
  localPlayer,
  remotePlayers,
  flags,
  vehicles,
}: {
  localPlayer: TeamPanelPlayer;
  remotePlayers: Record<string, TeamPanelPlayer>;
  flags: TeamPanelFlag[];
  vehicles: TeamPanelVehicle[];
}) {
  const localTeam = (localPlayer.team ?? '').toLowerCase();
  if (!localTeam) return null;
  const teammates: TeamPanelPlayer[] = [
    localPlayer,
    ...Object.values(remotePlayers).filter((p) => (p.team ?? '').toLowerCase() === localTeam),
  ].slice(0, 8);
  const accent = localTeam === 'red' ? '#ff5b5b' : '#62ddff';
  return (
    <div
      style={{
        position: 'fixed',
        left: 10,
        top: '38%',
        transform: 'translateY(-50%)',
        zIndex: 45,
        background: 'rgba(2,8,14,0.62)',
        border: `1px solid ${accent}44`,
        borderRadius: 4,
        padding: '6px 8px',
        fontFamily: 'monospace',
        fontSize: 10.5,
        color: '#d6e7f1',
        minWidth: 168,
        pointerEvents: 'none',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
      }}
    >
      <div style={{ color: accent, letterSpacing: 1.5, fontSize: 9, marginBottom: 4, textTransform: 'uppercase' }}>
        Squad · {teammates.length}
      </div>
      {teammates.map((p, idx) => {
        const status = statusForTeammate(p, vehicles, flags);
        const hp = Math.max(0, Math.min(100, Math.round(((p.health ?? 0) / 100) * 100)));
        const name = (p.displayName ?? `P${idx}`).slice(0, 11);
        const isLocal = p.id === localPlayer.id;
        return (
          <div
            key={`team-${p.id ?? idx}`}
            style={{ display: 'grid', gridTemplateColumns: '12px 1fr 36px 22px', gap: 4, alignItems: 'center', padding: '1px 0' }}
          >
            <span style={{ color: status.tone, textAlign: 'center' }}>{status.icon}</span>
            <span style={{ color: isLocal ? accent : '#d6e7f1', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {name}
            </span>
            <div style={{ height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${hp}%`,
                  height: '100%',
                  background: hp > 50 ? '#66dd99' : hp > 25 ? '#ffaa55' : '#ff5555',
                  transition: 'width 0.2s ease',
                }}
              />
            </div>
            <span style={{ color: 'rgba(214,231,241,0.55)', fontSize: 8.5, textAlign: 'right' }}>
              {roleLabelForPlayer(p)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Phase 159 — Spectator overlay ────────────────────────────────────────────
// While the local player is dead and the respawn screen is up, allow ←/→ to
// cycle through alive friendly players' viewpoints. Shows "Spectating: <name>".

function SpectatorOverlay({
  localPlayer,
  remotePlayers,
}: {
  localPlayer: {
    id?: string;
    team?: string | null;
    respawnTimer?: number;
    health?: number;
  };
  remotePlayers: Record<string, { id?: string; displayName?: string; team?: string | null; health?: number; respawnTimer?: number }>;
}) {
  const isDead = (localPlayer.respawnTimer ?? 0) > 0 || (localPlayer.health ?? 0) <= 0;
  const localTeam = (localPlayer.team ?? '').toLowerCase();
  const aliveTeammates = Object.values(remotePlayers).filter(
    (p) =>
      (p.team ?? '').toLowerCase() === localTeam &&
      (p.respawnTimer ?? 0) <= 0 &&
      (p.health ?? 100) > 0 &&
      p.id !== localPlayer.id,
  );
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!isDead || aliveTeammates.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIndex((i) => (i - 1 + aliveTeammates.length) % aliveTeammates.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setIndex((i) => (i + 1) % aliveTeammates.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDead, aliveTeammates.length]);

  if (!isDead || aliveTeammates.length === 0) return null;
  const target = aliveTeammates[Math.min(index, aliveTeammates.length - 1)];
  if (!target) return null;
  const accent = localTeam === 'red' ? '#ff5b5b' : '#62ddff';
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 110,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1001,
        background: 'rgba(2,8,14,0.78)',
        border: `1px solid ${accent}55`,
        padding: '6px 14px',
        fontFamily: 'monospace',
        fontSize: 12,
        color: '#fff',
        letterSpacing: 1.5,
        borderRadius: 4,
        pointerEvents: 'none',
        textAlign: 'center',
        minWidth: 220,
      }}
    >
      <div style={{ color: accent, fontSize: 9, letterSpacing: 2, marginBottom: 3 }}>SPECTATING</div>
      <div style={{ fontSize: 14, fontWeight: 700 }}>{target.displayName ?? 'Teammate'}</div>
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>
        ← / → cycle ({index + 1}/{aliveTeammates.length})
      </div>
    </div>
  );
}
